/**
 * components/agents/RunDrawer.jsx
 * Shared execution drawer — used by both Agents.jsx and Workflows.jsx.
 *
 * Props:
 *   target        — agent or chain object to run
 *   targetType    — "agent" | "chain"
 *   onClose       — () => void
 *   gmailStatus   — { connected: bool, email: string } | null
 *   agents        — full agents array (needed for chain step resolution)
 */

import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createPortal } from "react-dom";
import SpaceFeed from "../../components/SpaceFeed.jsx";
import DomainAgentChips from "../dashboard/DomainAgentChips.tsx";
import {
  X, Send, Copy, RefreshCw, Loader2, Check, Plus,
  ChevronDown, ChevronRight, Settings, MessageSquare,
  Activity, Code2, Clock, CheckCircle,
  Cpu, Wrench, Zap, Mail, Plug, Brain, Play, Bot,
  Paperclip, AlertCircle, Maximize2,
} from "lucide-react";
import { useComposerPicks, ToolsPicker, KnowledgePicker, PickChips } from "./ComposerPickers.jsx";
import { AttachmentCards, AttachmentViewer } from "./AttachmentCards.jsx";
import api, { agentAPI, customAgentAPI, gmailAPI, calendarAPI, youtubeAPI, linkedinAPI, mcpAPI, chatSessionAPI, evalsAPI } from "../../services/api";
import { useAI } from "../../context/AIContext";
import toast from "react-hot-toast";
import { TwitterCredModal } from "../../pages/IntegrationsPage.jsx";
import GitHubCredModal from "../modals/GitHubCredModal.jsx";
import GitLabCredModal from "../modals/GitLabCredModal.jsx";
import FigmaPatModal from "../modals/FigmaPatModal.jsx";
import EngramKeyModal from "../modals/EngramKeyModal.jsx";
import { alpha } from '../../utils/alpha';
import ApiKeyPrompt from "./ApiKeyPrompt.jsx";
import { isChannelConnected } from "../../services/notificationChannels";
import ModelFailureModal from "./ModelFailureModal.jsx";
import TaskResultCards, { tasksOf, introOf } from "./TaskResultCards.jsx";
import { analyzeModelFailure, redactSecrets } from "../../utils/modelFailure";
import { useDomainAgentSelection } from '../../hooks/useDomainAgentSelection.ts';

// Matches integrationStatusService.js's connected-integration keys.
// `kind` decides how the chat's inline "Connect" card connects it without
// navigating away from the conversation:
//   'modal'  — a self-contained credential-paste modal (see components/modals/)
//   'oauth'  — opened as a popup window; the server's OAuth callback routes
//              (routes/calendar.js, gmail.js, auth.js's linkedin branch) post
//              the result back via window.opener and close themselves
//              (see pages/OAuthPopupCallback.tsx) instead of redirecting.
//   'navigate' — no popup-safe flow exists yet, falls back to the old
//              full-page navigation rather than being silently broken.
//              (Instagram's OAuth callback always mints a fresh login JWT —
//              see routes/auth.js's instagram/callback — so it isn't safe
//              to treat as a simple "attach to my existing account" popup
//              the way calendar/gmail/linkedin already are.)
const INTEGRATION_META = {
  gmail:     { label: "Gmail",           kind: "oauth", connect: () => gmailAPI.connect(),    urlField: "url" },
  calendar:  { label: "Google Calendar", kind: "oauth", connect: () => calendarAPI.connect(), urlField: "url" },
  youtube:   { label: "YouTube",         kind: "oauth", connect: () => youtubeAPI.connect(),  urlField: "url" },
  linkedin:  { label: "LinkedIn",        kind: "oauth", connect: () => linkedinAPI.connect(), urlField: "authUrl" },
  instagram: { label: "Instagram",       kind: "navigate" },
  twitter:   { label: "Twitter / X",     kind: "modal", Modal: TwitterCredModal },
  github:    { label: "GitHub",          kind: "modal", Modal: GitHubCredModal },
  gitlab:    { label: "GitLab",          kind: "modal", Modal: GitLabCredModal },
  figma:     { label: "Figma",           kind: "modal", Modal: FigmaPatModal },
  engram:    { label: "Engram Memory",   kind: "modal", Modal: EngramKeyModal },
};
// Kept for anything reading the old export shape — INTEGRATION_META is the
// source of truth now.
const INTEGRATION_LABELS = Object.fromEntries(Object.entries(INTEGRATION_META).map(([k, v]) => [k, v.label]));

// Home chat can turn an explicit automation request into the same canvas-flow
// document used by the visual builder. This is deliberately conservative: a
// normal question is never silently converted into a workflow.
function workflowDraftFromPrompt(prompt) {
  const text = String(prompt || "").trim();
  const hasAutomationIntent = /\b(workflow|automate|automation|when(?:ever)?\s+i\s+(?:receive|get))\b/i.test(text);
  const channelMatch = text.match(/\b(email|telegram|whatsapp)\b/i);
  if (!hasAutomationIntent || !channelMatch) return null;

  const channel = channelMatch[1].toLowerCase();
  const taskTitle = text.match(/\b(?:create\s+(?:a\s+)?task|task\s+(?:is\s+)?created)[\s\S]{0,80}?\b(?:with\s+)?title\s+["“']([^"”']+)["”']/i)?.[1]
    || text.match(/\btask\s+(?:named|called)\s+["“']([^"”']+)["”']/i)?.[1]
    || "New task from workflow";
  const wantsTask = /\b(productivity|create\s+(?:a\s+)?task|task\s+(?:is\s+|should\s+be\s+|will\s+be\s+)?created|task\s+(?:with\s+)?title)\b/i.test(text);
  const agentId = wantsTask ? "domain:productivity" : /\bfinance\b/i.test(text) ? "finance" : /\b(hr|resume|candidate)\b/i.test(text) ? "research" : "writing";
  const agentName = wantsTask ? "Productivity · Create task" : agentId === "finance" ? "Finance Agent" : agentId === "research" ? "Research Agent" : "Writing Agent";
  const senderEmail = text.match(/\bfrom\s+([\w.+-]+@[\w.-]+\.[A-Za-z]{2,})\b/i)?.[1] || "";
  const subjectFilter = text.match(/\bword\s+["“']([^"”']+)["”']/i)?.[1]
    || text.match(/\b(?:subject|title)\b[^"“']*(?:word\s+)?["“']([^"”']+)["”']/i)?.[1]
    || "";
  const delivery = /\b(?:send|reply|email)\b.*\b(?:to|at)\s+[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/i.test(text)
    ? (/\b(?:reply|telegram|whatsapp)\b/i.test(text) && !/\bemail\b/i.test(text.replace(channelMatch[0], "")) ? "telegramSend" : "gmail")
    : null;
  const recipient = text.match(/\b(?:to|email)\s+([\w.+-]+@[\w.-]+\.[A-Za-z]{2,})/i)?.[1] || "";
  const sender = senderEmail || text.match(/\bfrom\s+([\w .'-]+?)(?=\s+(?:then|and|,|to)\b|$)/i)?.[1]?.trim() || "";
  const requestedName = text.match(/\bworkflow\s+(?:named|called)\s+["“']([^"”']+)["”']/i)?.[1]?.trim() || "";
  const title = requestedName || `${agentName.replace(" Agent", "")} ${channel} workflow`;
  const nodes = [
    { id: "n1", agid: "mobile-trigger", x: 80, y: 140, brief: `${channel}${sender ? ` from ${sender}` : ""}`, triggerChannel: channel, triggerRule: senderEmail ? "from" : subjectFilter ? "subject" : "", triggerValue: senderEmail || subjectFilter, triggerSubject: subjectFilter, triggerEnabled: false, item: { name: "Trigger", tag: "Email, Telegram, WhatsApp" } },
    { id: "n2", agid: agentId, x: 410, y: 140, brief: wantsTask ? `Create a Productivity task titled "${taskTitle}". Use the incoming message only as context; do not create duplicate tasks.` : "Analyse the incoming message", item: { name: agentName, tag: wantsTask ? "Domain agent" : "AI agent" } },
  ];
  const connections = [
    { from: "n1", to: "n2", port: "out", label: `New ${channel}` },
  ];
  if (delivery) {
    nodes.push({ id: "n3", agid: delivery, x: 720, y: 140, brief: recipient ? `Send to ${recipient}` : "Choose recipient", toEmail: delivery === "gmail" ? recipient : "", item: { name: delivery === "gmail" ? "Gmail Send" : "Telegram Send", tag: "Delivery" } });
    connections.push({ from: "n2", to: "n3", port: "out", label: "Agent result" });
  }
  return { title, nodes, connections, channel, recipient, taskTitle, createsTask: wantsTask };
}

// ── Markdown renderer ───────────────────────────────────────────────────────
function renderMarkdown(text) {
  let s = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/(?<!\*)\*(?!\*)([^*]+)\*(?!\*)/g, '<em>$1</em>');
  s = s.replace(/`([^`]+)`/g, '<code style="background:var(--app-surface-3);border-radius:4px;padding:0 4px;font-family:monospace;font-size:12px">$1</code>');
  s = s.replace(/^\s*[-*]\s+(.+)$/gm, '<li style="margin-left:16px;list-style:disc;margin-bottom:2px">$1</li>');
  // Images (generated by the agent): only https URLs or this app's own /api/generated files; no quotes/brackets can leave the attribute.
  s = s.replace(/!\[([^\]"<>]*)\]\(((?:https:\/\/|\/api\/generated\/)[^)\s"<>]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer"><img src="$2" alt="$1" loading="lazy" style="display:block;max-width:100%;max-height:420px;border-radius:10px;margin:6px 0;border:1px solid var(--lyra-color-border)" /></a>');
  s = s.replace(/\[([^\]]+)\]\(sandbox:\/([^)]+)\)/g,
    '<a class="sandbox-dl-link" data-filename="$2" href="#" style="color:var(--lyra-color-warning);font-weight:500;text-decoration:underline;cursor:pointer" title="Sandbox file (click for info)">$1 ⚠</a>');
  s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" style="color:var(--lyra-color-primary);text-decoration:underline;cursor:pointer">$1</a>');
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" style="color:var(--lyra-color-primary);text-decoration:underline;cursor:pointer">$1</a>');
  s = s.replace(/\n/g, '<br>');
  return s;
}

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  page:        "var(--lyra-color-surface)",
  surface:     "var(--lyra-color-surface)",
  surface2:    "var(--lyra-color-surface-2)",
  surface3:    "var(--app-surface-3)",
  border:      "var(--lyra-color-border)",
  border2:     "var(--app-border-strong)",
  ink:         "var(--lyra-color-text)",
  ink2:        "var(--app-text-2)",
  ink3:        "var(--lyra-color-text-muted)",
  ink4:        "var(--app-text-3)",
  accent:      "var(--lyra-color-primary)",
  accentLight: "var(--app-primary-soft)",
  accentMid:   "var(--app-primary-soft-2)",
  accentDark:  "var(--app-primary-deep)",
  green:       "var(--lyra-color-success)",
  greenBg:     "var(--lyra-color-success-bg)",
  greenBd:     "var(--app-success-border)",
  greenText:   "#14532D",
  violet:      "var(--lyra-color-primary)",
  violetBg:    "var(--app-primary-soft)",
  violetBd:    "var(--app-primary-soft-2)",
  violetText:  "var(--app-primary-deep)",
  red:         "var(--lyra-color-danger)",
  redBg:       "var(--lyra-color-danger-bg)",
  redBd:       "var(--app-danger-border)",
  amber:       "var(--lyra-color-warning)",
  amberBg:     "var(--lyra-color-warning-bg)",
  amberBd:     "var(--app-warning-border)",
  blue:        "var(--lyra-color-primary)",
  blueBg:      "var(--app-primary-soft)",
  blueBd:      "var(--app-primary-soft-2)",
};

// ── Trace step metadata ───────────────────────────────────────────────────────
const STEP_META = {
  user_input:      { color: T.blue,       bg: T.blueBg,     bd: T.blueBd,    Icon: MessageSquare },
  agent_reasoning: { color: T.violet,     bg: T.violetBg,   bd: T.violetBd,  Icon: Cpu           },
  tool_call:       { color: T.amber,      bg: T.amberBg,    bd: T.amberBd,   Icon: Wrench        },
  tool_response:   { color: T.green,      bg: T.greenBg,    bd: T.greenBd,   Icon: Zap           },
  final_output:    { color: T.accent,     bg: T.accentLight, bd: T.accentMid, Icon: CheckCircle  },
};

function AgentIcon({ icon, size = 24 }) {
  if (typeof icon === "string" && icon.startsWith("data:")) {
    return <img src={icon} alt="" style={{ width: size, height: size, objectFit: "cover", borderRadius: "inherit", display: "block" }} />;
  }
  return icon || null;
}

// ── Chain progress strip ──────────────────────────────────────────────────────
/**
 * ChainProgressStrip
 * Renders a horizontal step-by-step progress bar for chain (workflow) execution.
 *
 * Props:
// Local target state (allows global RunDrawer to open for chains/flows)
 *   agents    — full agents array  (used to resolve name/icon)
 *   statuses  — string[]  "waiting" | "running" | "done" | "error"  (one per step)
 *   activeIdx — number | null  (-1 = finished)
 */
function ChainProgressStrip({ steps, agents, statuses, activeIdx }) {
  if (!steps || steps.length === 0) return null;

  return (
    <>
      <style>{`
        @keyframes chainPulse {
          0%   { box-shadow: 0 0 0 0   ${alpha(T.accent, '55')}, 0 0 0 0   ${alpha(T.accentMid, '33')}; }
          60%  { box-shadow: 0 0 0 5px ${alpha(T.accent, '22')}, 0 0 0 10px ${alpha(T.accentMid, '11')}; }
          100% { box-shadow: 0 0 0 0   ${alpha(T.accent, '00')}, 0 0 0 0   ${alpha(T.accentMid, '00')}; }
        }
        @keyframes chainSpinner {
          to { transform: rotate(360deg); }
        }
        @keyframes chainFadeIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
      `}</style>

      <div style={{
        flexShrink: 0,
        padding: "10px 18px",
        borderBottom: `1px solid ${T.border}`,
        background: T.surface,
        animation: "chainFadeIn .2s ease",
        overflowX: "auto",
      }}>
       

        {/* Nodes + connectors */}
        <div style={{ display: "flex", alignItems: "center", gap: 0, minWidth: "max-content" }}>
          {steps.map((step, i) => {
            const status     = statuses[i] || "waiting";
            const agentEntry = agents.find(a => a._id === step.agentId || a.id === step.agentId);
            const name       = agentEntry?.name || step.label || `Step ${i + 1}`;
            const icon       = agentEntry?.icon || "🤖";
            const isLast     = i === steps.length - 1;

            // ── per-status visual config ──────────────────────────────────
            const cfg = (() => {
              switch (status) {
                case "running": return {
                  bg:     T.accentLight,
                  border: T.accent,
                  color:  T.accent,
                  ring:   true,
                  iconEl: (
                    <Loader2 size={11}
                      style={{ animation: "chainSpinner .9s linear infinite", color: T.accent, flexShrink: 0 }} />
                  ),
                };
                case "done": return {
                  bg:     T.greenBg,
                  border: T.greenBd,
                  color:  T.greenText,
                  ring:   false,
                  iconEl: <Check size={11} color={T.green} style={{ flexShrink: 0 }} />,
                };
                case "error": return {
                  bg:     T.redBg,
                  border: T.redBd,
                  color:  T.red,
                  ring:   false,
                  iconEl: <AlertCircle size={11} color={T.red} style={{ flexShrink: 0 }} />,
                };
                default: return {           // "waiting"
                  bg:     T.surface,
                  border: T.border2,
                  color:  T.ink4,
                  ring:   false,
                  iconEl: null,
                };
              }
            })();

            // connector color: green once step i is done, else border
            const connectorColor = status === "done" || statuses[i] === "done" ? T.greenBd : T.border2;

            return (
              <React.Fragment key={i}>
                {/* ── Node ── */}
                <div style={{
                  display:       "flex",
                  alignItems:    "center",
                  gap:           5,
                  padding:       "5px 10px",
                  borderRadius:  99,
                  border:        `1.5px solid ${cfg.border}`,
                  background:    cfg.bg,
                  color:         cfg.color,
                  fontSize:      11,
                  fontWeight:    status === "running" ? 600 : 400,
                  whiteSpace:    "nowrap",
                  maxWidth:      148,
                  transition:    "background .2s, border-color .2s, color .2s",
                  animation:     cfg.ring ? "chainPulse 1.4s ease-in-out infinite" : "none",
                  flexShrink:    0,
                  userSelect:    "none",
                }}>
                  {/* Agent emoji/icon */}
                  <span style={{ fontSize: 13, lineHeight: 1, flexShrink: 0 }}>
                    {typeof icon === "string" && !icon.startsWith("data:") ? icon : "🤖"}
                  </span>

                  {/* Name — truncated */}
                  <span style={{
                    overflow:     "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace:   "nowrap",
                    maxWidth:     88,
                    display:      "block",
                  }}>
                    {name}
                  </span>

                  {/* Status icon */}
                  {cfg.iconEl}
                </div>

                {/* ── Connector arrow (not after last node) ── */}
                {!isLast && (
                  <div style={{
                    flexShrink:  0,
                    display:     "flex",
                    alignItems:  "center",
                    width:       28,
                    position:    "relative",
                  }}>
                    {/* Line */}
                    <div style={{
                      height:     1.5,
                      flex:       1,
                      background: connectorColor,
                      transition: "background .3s",
                    }} />
                    {/* Arrowhead */}
                    <svg width="7" height="9" viewBox="0 0 7 9" fill="none"
                      style={{ flexShrink: 0, marginLeft: -1 }}>
                      <path d="M1 1l5 3.5L1 8" stroke={connectorColor} strokeWidth="1.5"
                        strokeLinecap="round" strokeLinejoin="round" style={{ transition: "stroke .3s" }} />
                    </svg>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ── Gmail send modal ──────────────────────────────────────────────────────────
function GmailModal({ result, fromEmail, initialTo, onClose }) {
  const raw         = typeof result === "string" ? result : JSON.stringify(result ?? "", null, 2);
  const getSubject  = t => { const m = t.match(/^Subject:\s*(.+)/mi); return m ? m[1].trim() : "Email from AI Agent"; };
  const getBody     = t => t.replace(/^Subject:.*\n?/mi, "").trim();
  const [form, setForm]     = useState({ to: initialTo || "", subject: getSubject(raw), body: getBody(raw) });
  const [sending, setSending] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSend = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.to.trim())) { toast.error("Valid recipient email required"); return; }
    if (!form.subject.trim() || !form.body.trim()) { toast.error("Subject and body required"); return; }
    setSending(true);
    try {
      await gmailAPI.send({ to: form.to.trim(), subject: form.subject.trim(), body: form.body.trim() });
      toast.success(`Sent to ${form.to.trim()}`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || "Send failed");
    } finally { setSending(false); }
  };

  const inputSt = { width: "100%", padding: "8px 11px", fontSize: 13, border: `1px solid ${T.border}`, borderRadius: 8, background: T.surface, color: T.ink, outline: "none", fontFamily: "inherit", boxSizing: "border-box" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1100, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: T.surface, borderRadius: 14, width: "100%", maxWidth: 500, border: `1px solid ${T.border}`, overflow: "hidden" }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: T.redBg, border: `1px solid ${T.redBd}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Mail size={15} color={T.red} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>Send via Gmail</div>
            <div style={{ fontSize: 11, color: T.ink4 }}>From: {fromEmail}</div>
          </div>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${T.border}`, background: T.surface2, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.ink3 }}>
            <X size={13} />
          </button>
        </div>
        <div style={{ padding: "16px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
          {[{ k: "to", label: "To", placeholder: "recipient@example.com" }, { k: "subject", label: "Subject", placeholder: "Email subject" }].map(({ k, label, placeholder }) => (
            <div key={k}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.ink3, marginBottom: 5 }}>{label}</label>
              <input value={form[k]} onChange={set(k)} placeholder={placeholder} style={inputSt} />
            </div>
          ))}
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.ink3, marginBottom: 5 }}>Message</label>
            <textarea value={form.body} onChange={set("body")} rows={7}
              style={{ ...inputSt, resize: "vertical", lineHeight: 1.6 }} />
          </div>
          <button onClick={handleSend} disabled={sending}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 0", borderRadius: 9, border: "none", background: sending ? T.border : T.red, color: sending ? T.ink4 : "var(--lyra-color-surface)", fontSize: 13, fontWeight: 500, cursor: sending ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
            {sending ? <><Loader2 size={13} style={{ animation: "spin 1s linear infinite" }} /> Sending…</> : <><Send size={13} /> Send Email</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Debug timeline ────────────────────────────────────────────────────────────
function DebugTimeline({ steps }) {
  const [collapsed, setCollapsed] = useState(new Set());
  const toggle = id => setCollapsed(prev => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });

  if (!steps.length) return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, padding: 24, minHeight: 150 }}>
      <Activity size={28} color={T.border2} />
      <div style={{ fontSize: 13, fontWeight: 500, color: T.ink3 }}>No trace yet</div>
      <div style={{ fontSize: 11.5, color: T.ink4 }}>Send a message to see execution steps</div>
    </div>
  );

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "12px", display: "flex", flexDirection: "column", gap: 0, borderRadius: 10, background: T.surface2, border: `1px solid ${T.border}` }}>
      {steps.map((step, idx) => {
        const meta       = STEP_META[step.type] || STEP_META.final_output;
        const { Icon }   = meta;
        const isExpanded = !collapsed.has(step.id);
        const isLast     = idx === steps.length - 1;
        return (
          <div key={step.id} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, paddingTop: 2 }}>
              <div style={{ width: 30, height: 30, borderRadius: "50%", background: meta.bg, border: `1.5px solid ${meta.bd}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon size={14} color={meta.color} />
              </div>
              {!isLast && <div style={{ width: 2, flex: 1, minHeight: 16, background: T.border, marginTop: 2 }} />}
            </div>
            <div style={{ flex: 1, marginBottom: isLast ? 0 : 10 }}>
              <div onClick={() => toggle(step.id)}
                style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "8px 12px", borderRadius: isExpanded ? "10px 10px 0 0" : 10, background: T.surface, border: `1px solid ${T.border}` }}>
                <span style={{ flex: 1, fontSize: 12.5, fontWeight: 600, color: T.ink }}>{step.label}</span>
                {step.latencyMs !== undefined && (
                  <span style={{ fontSize: 10.5, color: T.ink4, display: "flex", alignItems: "center", gap: 3 }}>
                    <Clock size={9} />{step.latencyMs}ms
                  </span>
                )}
                <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 99, background: step.status === "success" ? T.greenBg : T.redBg, color: step.status === "success" ? T.green : T.red, border: `1px solid ${step.status === "success" ? T.greenBd : T.redBd}` }}>
                  {step.status}
                </span>
                {isExpanded ? <ChevronDown size={13} color={T.ink4} /> : <ChevronRight size={13} color={T.ink4} />}
              </div>
              {isExpanded && (
                <div style={{ border: `1px solid ${T.border}`, borderTop: "none", borderRadius: "0 0 10px 10px", overflow: "hidden" }}>
                  {[{ label: "Input", val: step.input }, { label: "Output", val: step.output }].map(({ label, val }) => (
                    <div key={label} style={{ padding: "10px 12px", borderBottom: label === "Input" ? `1px solid ${T.border}` : "none", background: T.surface }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: T.ink4, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: val != null ? 6 : 0 }}>{label}</div>
                      {val != null
                        ? <pre style={{ margin: 0, fontSize: 11, color: T.ink2, lineHeight: 1.55, background: T.surface2, padding: "8px 10px", borderRadius: 7, overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all", border: `1px solid ${T.border}`, maxHeight: 140, overflowY: "auto", fontFamily: "ui-monospace, monospace" }}>
                            {typeof val === "string" ? val : JSON.stringify(val, null, 2)}
                          </pre>
                        : <span style={{ fontSize: 11.5, color: T.ink4 }}>—</span>
                      }
                    </div>
                  ))}
                  {step.tokens && (
                    <div style={{ padding: "7px 12px", background: T.surface2, borderTop: `1px solid ${T.border}`, display: "flex", gap: 16 }}>
                      {step.tokens.prompt     !== undefined && <span style={{ fontSize: 10.5, color: T.ink4 }}>Prompt: <strong style={{ color: T.ink3 }}>{step.tokens.prompt}</strong></span>}
                      {step.tokens.completion !== undefined && <span style={{ fontSize: 10.5, color: T.ink4 }}>Completion: <strong style={{ color: T.ink3 }}>{step.tokens.completion}</strong></span>}
                      {step.tokens.total      !== undefined && <span style={{ fontSize: 10.5, color: T.ink4 }}>Total: <strong style={{ color: T.ink3 }}>{step.tokens.total}</strong></span>}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── JSON view ─────────────────────────────────────────────────────────────────
function JsonView({ payloads }) {
  const [active, setActive] = useState(0);
  if (!payloads.length) return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, padding: 24, minHeight: 150 }}>
      <Code2 size={28} color={T.border2} />
      <div style={{ fontSize: 13, fontWeight: 500, color: T.ink3 }}>No payloads yet</div>
      <div style={{ fontSize: 11.5, color: T.ink4 }}>Send a message to see raw data</div>
    </div>
  );
  const idx  = Math.min(active, payloads.length - 1);
  const json = JSON.stringify(payloads[idx].payload, null, 2);
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface2 }}>
      <div style={{ display: "flex", borderBottom: `1px solid ${T.border}`, padding: "6px 8px 0", flexShrink: 0, gap: 4, flexWrap: "wrap" }}>
        {payloads.map((p, i) => (
          <button key={p.id} onClick={() => setActive(i)}
            style={{ padding: "6px 10px", fontSize: 11, fontWeight: idx === i ? 600 : 500, color: idx === i ? T.accent : T.ink3, border: `1px solid ${idx === i ? T.accentMid : T.border}`, borderRadius: 999, background: idx === i ? T.accentLight : T.surface, cursor: "pointer", marginBottom: 6, fontFamily: "inherit" }}>
            {p.label}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: "12px", position: "relative" }}>
        <button onClick={() => { navigator.clipboard.writeText(json); toast.success("Copied!"); }}
          style={{ position: "absolute", top: 20, right: 20, zIndex: 1, display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, color: T.ink3, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>
          <Copy size={11} /> Copy
        </button>
        <pre style={{ margin: 0, fontSize: 11, color: T.ink2, lineHeight: 1.6, background: T.surface, padding: "14px 16px", borderRadius: 10, whiteSpace: "pre-wrap", wordBreak: "break-all", border: `1px solid ${T.border}`, fontFamily: "ui-monospace, monospace" }}>
          {json}
        </pre>
      </div>
    </div>
  );
}

// ── Home chat evaluation summary ───────────────────────────────────────────
// Home uses the same persisted AgentRun and checks endpoint as Mission Control.
// It intentionally does not recreate traces locally: the independent report is
// the single source of truth for completed chat-run validation.
function HomeRunEvaluation({ runId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    if (!runId) return;
    try { setData(await evalsAPI.runChecks(runId)); setError(''); }
    catch (_) { setError('Evaluation is not available for this run.'); }
  }, [runId]);

  useEffect(() => { setData(null); setError(''); load(); }, [load]);
  const deliveryState = data?.externalEvaluation?.state;
  const waiting = ['pending', 'processing'].includes(deliveryState);
  useEffect(() => {
    if (!waiting) return undefined;
    const timer = window.setInterval(load, 2500);
    return () => window.clearInterval(timer);
  }, [waiting, load]);

  const header = (title) => <div style={{ fontSize: 10.5, color: T.ink4, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', marginBottom: 8 }}>{title}</div>;
  if (!runId) return <div style={{ padding: '28px 18px', textAlign: 'center', color: T.ink4 }}><CheckCircle size={26} color={T.accentMid} style={{ display: 'block', margin: '0 auto 10px' }} /><div style={{ fontSize: 13, fontWeight: 650, color: T.ink3 }}>Run evaluation</div><div style={{ fontSize: 11.5, marginTop: 5 }}>Send a message to see checks and the independent evaluation here.</div></div>;
  if (!data && !error) return <div style={{ padding: 26, textAlign: 'center' }}><Loader2 size={18} color={T.accent} style={{ animation: 'spin 1s linear infinite' }} /></div>;
  if (error) return <div style={{ padding: 18, fontSize: 12.5, color: T.red }}>{error}</div>;

  const external = data.externalEvaluation;
  const report = external?.report;
  const judge = report?.judgment;
  const deliveryFailed = external?.state === 'dead' || judge?.status === 'error';
  return <div style={{ padding: 12 }}>
    {header(`Automatic checks · ${data.passed}/${data.total} passed`)}
    <div style={{ display: 'grid', gap: 7 }}>
      {data.checks.map((check) => <div key={check.id} style={{ display: 'flex', gap: 7, alignItems: 'flex-start', fontSize: 12 }}>
        {check.pass ? <Check size={14} color={T.green} style={{ flexShrink: 0, marginTop: 1 }} /> : <X size={14} color={T.red} style={{ flexShrink: 0, marginTop: 1 }} />}
        <div><b style={{ color: T.ink }}>{check.label}</b><div style={{ color: check.pass ? T.ink4 : T.red, marginTop: 1 }}>{check.detail}</div></div>
      </div>)}
    </div>
    <div style={{ marginTop: 13, paddingTop: 11, borderTop: `1px solid ${T.border}` }}>
      {header('Independent evaluation')}
      <div style={{ border: `1px solid ${deliveryFailed ? T.redBd : T.border}`, background: deliveryFailed ? T.redBg : T.surface2, borderRadius: 9, padding: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700, color: deliveryFailed ? T.red : T.ink }}>
          {waiting ? <Loader2 size={15} color={T.accent} style={{ animation: 'spin 1s linear infinite' }} /> : deliveryFailed ? <AlertCircle size={15} /> : <CheckCircle size={15} color={T.green} />}
          {waiting ? 'Evaluation in progress' : external ? `Delivery: ${external.state}` : 'Awaiting evaluation'}
        </div>
        {waiting && <div role="progressbar" aria-label="Loading external evaluation" aria-valuetext="Loading evaluation report" style={{ marginTop: 10, height: 5, overflow: 'hidden', borderRadius: 99, background: T.accentLight }}><div style={{ width: '44%', height: '100%', borderRadius: 99, background: `linear-gradient(90deg, transparent, ${T.accent}, ${T.accentDark}, ${T.accent}, transparent)`, animation: 'rdEvalProgress 1.45s cubic-bezier(.4,0,.2,1) infinite' }} /></div>}
        {report?.checks?.length > 0 && <div style={{ marginTop: 9, display: 'grid', gap: 5 }}>{report.checks.map((check, i) => <div key={`${check.name}-${i}`} style={{ fontSize: 11.5, color: check.status === 'pass' ? T.greenText : T.ink3 }}><b>{check.name.replaceAll('_', ' ')}</b> · {check.status}{check.observed != null ? ` · ${check.observed}` : ''}</div>)}</div>}
        {judge?.status && <div style={{ fontSize: 11.5, color: judge.status === 'error' ? T.red : T.ink3, marginTop: 9 }}>Judge: {judge.status}{judge.reason ? ` · ${judge.reason}` : ''}</div>}
        {external?.lastError && <div style={{ fontSize: 11.5, color: T.red, marginTop: 8 }}>Delivery error: {external.lastError}</div>}
      </div>
    </div>
  </div>;
}

// ── Context panel sections (collapsible) ───────────────────────────────────
function ContextSections({ runTrace, rawPayloads, runMemories, setRunMemories, lastResult, modelLabel, running, activeStepIdx, displayTarget }) {
  const [open, setOpen] = useState({ run: true, debug: false, json: false, memory: false, artifacts: false });
  const toggle = (k) => setOpen(prev => ({ ...prev, [k]: !prev[k] }));

  // Fetch memories when memory section opens
  useEffect(() => {
    if (!open.memory) return;
    const agentId = displayTarget?._id || displayTarget?.id;
    if (!agentId) return;
    (async () => {
      try {
        const r = await fetch('/api/engram/search', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: '.', scopeTags: [`agent:${agentId}`], onlyLatest: false, limit: 20, scoreThreshold: 0 }),
        });
        if (!r.ok) return;
        const d = await r.json();
        setRunMemories && setRunMemories(d.results ?? []);
      } catch (e) { /* ignore */ }
    })();
  }, [open.memory, displayTarget?._id, displayTarget?.id, setRunMemories]);

  const artifacts = [];
  if (lastResult) {
    if (Array.isArray(lastResult.files)) artifacts.push(...lastResult.files);
    if (Array.isArray(lastResult.generatedFiles)) artifacts.push(...lastResult.generatedFiles);
    if (lastResult.sandbox && Array.isArray(lastResult.sandbox.files)) artifacts.push(...lastResult.sandbox.files);
    if (lastResult.result && typeof lastResult.result === 'object' && Array.isArray(lastResult.result.files)) artifacts.push(...lastResult.result.files);
  }

  const runStatus = running ? 'running' : (activeStepIdx === -1 ? 'finished' : (activeStepIdx !== null ? 'running' : 'idle'));

  const sectionMeta = {
    run: { Icon: CheckCircle, subtitle: 'Current execution profile' },
    debug: { Icon: Activity, subtitle: 'Step-by-step diagnostics' },
    json: { Icon: Code2, subtitle: 'Raw request and response payloads' },
    memory: { Icon: Brain, subtitle: 'Agent memory retrieval context' },
    artifacts: { Icon: Paperclip, subtitle: 'Generated files and outputs' },
  };

  const SectionHeader = ({ title, k }) => (
    <div onClick={() => toggle(k)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', padding: '9px 10px', borderRadius: 10, background: open[k] ? T.accentLight : T.surface2, border: `1px solid ${open[k] ? T.accentMid : T.border}`, transition: 'all .16s ease' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 24, height: 24, borderRadius: 7, background: open[k] ? T.surface : 'var(--lyra-color-surface)', border: `1px solid ${open[k] ? T.accentMid : T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: open[k] ? T.accent : T.ink4 }}>
          {sectionMeta[k]?.Icon ? React.createElement(sectionMeta[k].Icon, { size: 13 }) : null}
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.ink }}>{title}</div>
          <div style={{ fontSize: 10.5, color: T.ink4 }}>{sectionMeta[k]?.subtitle || ''}</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {open[k] ? <ChevronDown size={14} color={T.ink4} /> : <ChevronRight size={14} color={T.ink4} />}
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <SectionHeader title="Run" k="run" />
        {open.run && (
          <div style={{ padding: 10, border: `1px solid ${T.border}`, borderTop: 'none', borderRadius: '0 0 10px 10px', background: T.surface }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>{displayTarget?.name || '—'}</div>
              <span style={{ fontSize: 10.5, fontWeight: 600, padding: '3px 8px', borderRadius: 999, background: runStatus === 'running' ? T.greenBg : T.surface2, color: runStatus === 'running' ? T.greenText : T.ink3, border: `1px solid ${runStatus === 'running' ? T.greenBd : T.border}` }}>{runStatus}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div style={{ padding: '7px 8px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface2 }}>
                <div style={{ fontSize: 10.5, color: T.ink4, marginBottom: 2 }}>Model</div>
                <div style={{ fontSize: 12, color: T.ink3, fontWeight: 600 }}>{modelLabel}</div>
              </div>
              <div style={{ padding: '7px 8px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface2 }}>
                <div style={{ fontSize: 10.5, color: T.ink4, marginBottom: 2 }}>Duration</div>
                <div style={{ fontSize: 12, color: T.ink3, fontWeight: 600 }}>{lastResult?.durationMs ? `${lastResult.durationMs}ms` : '—'}</div>
              </div>
              <div style={{ padding: '7px 8px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface2, gridColumn: '1 / span 2' }}>
                <div style={{ fontSize: 10.5, color: T.ink4, marginBottom: 2 }}>Run type</div>
                <div style={{ fontSize: 12, color: T.ink3, fontWeight: 600 }}>{displayTarget?.source === 'canvas' ? 'Flow' : (displayTarget?.steps ? 'Chain' : 'Agent')}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div>
        <SectionHeader title="Debug & Logs" k="debug" />
        {open.debug && (
          <div style={{ padding: 8, border: `1px solid ${T.border}`, borderTop: 'none', borderRadius: '0 0 10px 10px', background: T.surface, minHeight: 120 }}>
            <DebugTimeline steps={runTrace} />
          </div>
        )}
      </div>

      <div>
        <SectionHeader title="Raw JSON" k="json" />
        {open.json && (
          <div style={{ padding: 8, border: `1px solid ${T.border}`, borderTop: 'none', borderRadius: '0 0 10px 10px', background: T.surface, minHeight: 120 }}>
            <JsonView payloads={rawPayloads} />
          </div>
        )}
      </div>

      <div>
        <SectionHeader title="Memory" k="memory" />
        {open.memory && (
          <div style={{ padding: 8, border: `1px solid ${T.border}`, borderTop: 'none', borderRadius: '0 0 10px 10px', background: T.surface }}>
            {(!runMemories || runMemories.length === 0) ? (
              <div style={{ textAlign: 'center', padding: 20, color: T.ink4 }}>
                <Brain size={28} color={T.violetBd} style={{ margin: '0 auto 10px', display: 'block' }} />
                <div style={{ fontSize: 13, color: T.ink3 }}>No memories found for this agent.</div>
                <div style={{ fontSize: 11.5, color: T.ink4, marginTop: 6 }}>Run the agent to start building memory.</div>
                <div style={{ marginTop: 8 }}>
                  <button onClick={() => {
                    // refresh handled via effect; toggle to trigger fetch
                    setOpen(prev => ({ ...prev, memory: true }));
                  }} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 8, background: T.surface2, border: `1px solid ${T.border}`, cursor: 'pointer' }}>
                    <RefreshCw size={12} /> Refresh
                  </button>
                </div>
              </div>
            ) : (
              runMemories.map((m, i) => (
                <div key={i} style={{ padding: '9px 10px', marginBottom: 8, borderRadius: 10, background: T.violetBg, border: `1px solid ${T.violetBd}` }}>
                  <div style={{ fontSize: 12.5, color: T.ink, lineHeight: 1.5, marginBottom: 4 }}>{m.content}</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 10.5, fontWeight: 600, padding: '1px 6px', borderRadius: 4, background: T.violetBd, color: T.violetText }}>{m.type}</span>
                    {m.metadata?.source && <span style={{ fontSize: 10.5, color: T.ink4 }}>{m.metadata.source}</span>}
                    {m.score !== undefined && <span style={{ fontSize: 10.5, color: T.ink4 }}>relevance: {(m.score * 100).toFixed(0)}%</span>}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <div>
        <SectionHeader title="Artifacts" k="artifacts" />
        {open.artifacts && (
          <div style={{ padding: 8, border: `1px solid ${T.border}`, borderTop: 'none', borderRadius: '0 0 10px 10px', background: T.surface }}>
            {artifacts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20, color: T.ink4 }}>
                <div style={{ fontSize: 13, color: T.ink3 }}>No artifacts generated.</div>
              </div>
            ) : (
              artifacts.map((a, i) => (
                <div key={i} style={{ padding: '9px 10px', border: `1px solid ${T.border}`, borderRadius: 10, marginBottom: 8, background: T.surface2 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>{a.name || a.filename || a.id || `Artifact ${i + 1}`}</div>
                  <div style={{ fontSize: 11.5, color: T.ink4, marginTop: 5 }}>{a.contentType || a.type || ''}</div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main RunDrawer ────────────────────────────────────────────────────────────
// A fixed-position spot above an anchor element, so a dropdown opens over the page instead of being cut off by the composer's
// own clipping (overflow) or by the top of a short container.
// Renders its children where they are, or into another element of the page (the Home card's header) when one is given.
// An action the model proposed that changes something outside Lyra (e.g. send an email). Nothing has happened yet: the user
// reads it here and answers. The answer goes to the server, which sends it (or not) exactly once.
function ApprovalCard({ approval }) {
  // checking | pending | working | done | rejected | expired | error. The server is the source of truth, so a chat that is opened
  // again later shows what actually happened (sent, not sent, expired) and never offers an answered approval a second time.
  const [state, setState] = useState({ phase: 'checking' });
  useEffect(() => {
    let live = true;
    api.get(`/api/skills-registry/approvals/${approval.approvalId}`)
      .then((r) => {
        if (!live) return;
        const d = r.data || {};
        setState(d.status === 'pending' ? { phase: 'pending' }
          : d.status === 'executed' ? { phase: 'done', output: d.output }
          : d.status === 'rejected' ? { phase: 'rejected' }
          : d.status === 'expired' ? { phase: 'expired' }
          : d.status === 'approved' ? { phase: 'working' }
          : { phase: 'error', message: d.error || 'It could not be done.' });
      })
      .catch(() => { if (live) setState({ phase: 'pending' }); });   // cannot check: fall back to the old behaviour (the server still refuses a repeat)
    return () => { live = false; };
  }, [approval.approvalId]);
  const p = approval.preview || {};
  const rows = [['To', [].concat(p.to || []).join(', ')], ['Cc', [].concat(p.cc || []).join(', ')], ['Bcc', [].concat(p.bcc || []).join(', ')], ['Subject', p.subject || '']].filter(([, v]) => v);
  const answer = async (decision) => {
    setState({ phase: 'working' });
    try {
      const r = await api.post(`/api/skills-registry/approvals/${approval.approvalId}/decision`, { decision });
      const d = r.data || {};
      if (d.status === 'executed') setState({ phase: 'done', output: d.output });
      else if (d.status === 'rejected') setState({ phase: 'rejected' });
      else setState({ phase: 'error', message: d.error || 'It could not be done.' });
    } catch (e) {
      setState({ phase: 'error', message: e?.response?.data?.error || e?.message || 'It could not be done.' });
    }
  };
  const box = { marginTop: 8, border: `1px solid ${T.border}`, borderRadius: 12, background: T.surface2, overflow: 'hidden' };
  const btn = (primary) => ({ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', border: primary ? 'none' : `1px solid ${T.border}`, background: primary ? T.accent : 'transparent', color: primary ? 'var(--lyra-color-primary-fg, var(--app-on-accent))' : T.ink2 });
  return (
    <div role="group" aria-label="Approval needed" style={box}>
      <div style={{ padding: '8px 12px', borderBottom: `1px solid ${T.border}`, fontSize: 11.5, fontWeight: 700, color: T.ink2, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span>{state.phase === 'done' ? 'Sent' : state.phase === 'rejected' ? 'Not sent' : state.phase === 'expired' ? 'Expired' : state.phase === 'error' ? 'Could not send' : 'Approve this email?'}</span>
        <span style={{ fontWeight: 500, color: T.ink4 }}>{state.phase === 'pending' || state.phase === 'working' ? 'Nothing is sent until you approve' : ''}</span>
      </div>
      <div style={{ padding: '10px 12px', fontSize: 12.5, color: T.ink }}>
        {rows.map(([k, v]) => <div key={k} style={{ display: 'flex', gap: 8, marginBottom: 3 }}><span style={{ width: 52, color: T.ink4, flexShrink: 0 }}>{k}</span><span style={{ minWidth: 0, wordBreak: 'break-word' }}>{v}</span></div>)}
        {p.body !== undefined && <div style={{ marginTop: 6, padding: '8px 10px', borderRadius: 8, background: T.surface, border: `1px solid ${T.border}`, whiteSpace: 'pre-wrap', maxHeight: 180, overflowY: 'auto', fontSize: 12.5, lineHeight: 1.5 }}>{p.body}</div>}
      </div>
      <div style={{ padding: '0 12px 10px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {(state.phase === 'pending' || state.phase === 'working') && (
          <>
            <button type="button" disabled={state.phase === 'working'} onClick={() => answer('approve')} style={{ ...btn(true), opacity: state.phase === 'working' ? 0.6 : 1 }}>{state.phase === 'working' ? 'Sending…' : 'Approve & send'}</button>
            <button type="button" disabled={state.phase === 'working'} onClick={() => answer('reject')} style={btn(false)}>Don't send</button>
          </>
        )}
        {state.phase === 'done' && <span style={{ fontSize: 12, color: T.green }}>✓ Sent{state.output && state.output.message_id ? ` (message ${state.output.message_id})` : ''}</span>}
        {state.phase === 'rejected' && <span style={{ fontSize: 12, color: T.ink3 }}>You chose not to send it.</span>}
        {state.phase === 'expired' && <span style={{ fontSize: 12, color: T.ink3 }}>This request expired and was not sent. Ask again if you still want it.</span>}
        {state.phase === 'checking' && <span style={{ fontSize: 12, color: T.ink4 }}>Checking…</span>}
        {state.phase === 'error' && <span role="alert" style={{ fontSize: 12, color: T.red }}>{state.message}</span>}
      </div>
    </div>
  );
}

function HeaderSlot({ target, children }) { return target ? createPortal(children, target) : children; }

function useAnchoredAbove(open, anchorRef, { width = 340, maxHeight = 340 } = {}) {
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    if (!open || !anchorRef.current) { setPos(null); return undefined; }
    const place = () => {
      const r = anchorRef.current.getBoundingClientRect();
      const w = Math.min(width, window.innerWidth - 16);
      setPos({ position: 'fixed', left: Math.max(8, Math.min(r.left, window.innerWidth - w - 8)), bottom: window.innerHeight - r.top + 6, width: w, maxHeight: Math.max(160, Math.min(maxHeight, r.top - 16)) });
    };
    place();
    window.addEventListener('resize', place); window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open, anchorRef, width, maxHeight]);
  return pos;
}

export default function RunDrawer({
  target, targetType = "agent", onClose, gmailStatus, agents = [], spaceId = null,
  // variant: 'drawer' (default) = today's floating slide-in panel, unchanged.
  // 'page' = full-bleed, no floating chrome — used when reached fresh (e.g. Home → New Task),
  // shows pageHeading/pageSubheading/suggestions in place of the generic empty state,
  // and relocates model selection into the composer toolbar since there's no per-agent header yet.
  variant = "drawer",
  suggestions = [],
  agentSuggestions = [], // quick-pick agent pills (domain-matched) — clicking attaches instantly via addChip
  pageHeading = "What can I help you accomplish?",
  pageSubheading = "Set a goal, constraints, and output format. Lyra will run and stream the result here.",
  // page-mode only: fires after a chat auto-saves, so a sibling history rail
  // (see AIAssistantPage.tsx) can refresh its list without polling.
  onSessionSaved = null,
  // Domain agents (MCP, e.g. Workforce): the ones this conversation talks to, the connected ones that can
  // still be added, and the handlers. Their ids are sent with every request so the server routes to them.
  domainAgents: hostDomainAgents = [],
  availableDomainAgents: hostAvailableDomainAgents = [],
  onAddDomainAgent: hostOnAddDomainAgent = null,
  onRemoveDomainAgent: hostOnRemoveDomainAgent = null,
  // External registry agents are selected on Home and passed by immutable ids.
  externalAgentIds: hostExternalAgentIds = [],
  // page mode: when set, a button at the composer's top-right calls it (with the unsent text) to continue full screen
  onExpand = null,
  // page mode: false hides the heading/suggestions block while the chat is empty (the Home composer is just a box until used)
  showEmptyState = true,
  // page mode: size to the composer while the chat is empty (the Home box), instead of filling its parent
  compact = false,
  // page mode: false keeps the conversation out of the saved history (the Home composer's "Save conversations" switch)
  autosave = true,
  // page mode: false hides the "New task" button in the header (the host offers its own way to start over)
  showNewTask = true,
  // page mode: changing this value clears the conversation (a host's "New chat")
  resetKey = undefined,
  // page mode: tighter spacing (the Home chat); `wideChat` also lets the conversation use the full width instead of a centered column
  dense = false,
  wideChat = false,
  // page mode: an element of the host page to show the header buttons in (Context, copy, refresh) instead of a row inside the chat
  headerTarget = null,
}) {
  const embedded = variant === "page";
  // Domain agents: a host that manages the selection (Home) passes it in; anywhere else (the floating run drawer)
  // the drawer manages its own, so it has the same Domain agent picker as the Home chat.
  const hostManagesDomainAgents = !!(hostOnAddDomainAgent && hostOnRemoveDomainAgent);
  const ownDomainSelection = useDomainAgentSelection(null, { enabled: !hostManagesDomainAgents && targetType !== "chain" });
  const domainAgents = hostManagesDomainAgents ? hostDomainAgents : ownDomainSelection.selectedAgents;
  const availableDomainAgents = hostManagesDomainAgents ? hostAvailableDomainAgents : ownDomainSelection.available;
  const onAddDomainAgent = hostManagesDomainAgents ? hostOnAddDomainAgent : (targetType !== "chain" ? ownDomainSelection.toggle : null);
  const onRemoveDomainAgent = hostManagesDomainAgents ? hostOnRemoveDomainAgent : (targetType !== "chain" ? ownDomainSelection.remove : null);
  const domainAgentIds = domainAgents.map(a => a.id);
  const externalAgentIds = Array.isArray(hostExternalAgentIds) ? hostExternalAgentIds : [];
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // "Send to Telegram" on a reply is offered only when Telegram is connected.
  const [tgConnected, setTgConnected] = useState(false);
  useEffect(() => { let live = true; isChannelConnected('telegram').then((v) => { if (live) setTgConnected(!!v); }).catch(() => {}); return () => { live = false; }; }, []);
  const sendToTelegram = useCallback(async (text) => {
    try { await api.post('/telegram/send-me', { text }); toast.success('Sent to your Telegram'); }
    catch (e) { toast.error(e?.response?.data?.error || 'Could not send to Telegram'); }
  }, []);
  const { models, selectedMode, selectedModelId, customApiKeys, getEnabledModels, prefsLoaded } = useAI();
  // Nothing can be answered without a key for some model: ask for one instead of sending (and failing).
  const hasApiKey = Object.values(customApiKeys || {}).some((k) => typeof k === "string" && k.trim().length > 0);
  const [keyPromptOpen, setKeyPromptOpen] = useState(false);
  const [modelFailure, setModelFailure] = useState(null);
  // A run failed: when it is the user's keys/credits (no model could answer) open the fix-it modal, else toast as before.
  // Returns the failure summary, or null for any other error.
  const reportRunError = (msg) => {
    const f = analyzeModelFailure(msg);
    if (f) { setModelFailure({ ...f, raw: msg }); return f; }
    toast.error(redactSecrets(msg));
    return null;
  };
  // Coming back from the Models page: put the message they were about to send back in the composer.
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem("lyra:pending-prompt");
      if (saved) { sessionStorage.removeItem("lyra:pending-prompt"); setPrompt((p) => p || saved); }
    } catch (e) { /* storage blocked */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (hasApiKey) setKeyPromptOpen(false); }, [hasApiKey]);
  const enabledModels = getEnabledModels ? getEnabledModels() : (models || []);

  const [localModelId,    setLocalModelId]    = useState(null);
  const [showModelPicker, setShowModelPicker] = useState(false);
  const [prompt,          setPrompt]          = useState("");
  const [history,         setHistory]         = useState([]);
  const [running,         setRunning]         = useState(false);
  const [activeTab,       setActiveTab]       = useState("chat");
  const [runTrace,        setRunTrace]        = useState([]);
  const [runMemories,     setRunMemories]     = useState([]);
  const [rawPayloads,     setRawPayloads]     = useState([]);
  const [showGmailModal,  setShowGmailModal]  = useState(false);
  const [lastResult,      setLastResult]      = useState(null);
  const [mcpServers,      setMcpServers]      = useState([]);
  const [pendingFiles,    setPendingFiles]    = useState([]);
  // What this request is given: skills from the registry and Vault spaces (kept across Home and New Task), and files in flight.
  const picks = useComposerPicks();
  const [uploading, setUploading] = useState([]);
  // ids of the files sent with the current request (the composer's chips are cleared at send, the run still needs them)
  const attachRef = useRef([]);
  const [viewing, setViewing] = useState(null);   // the attached file open in the viewer
  const [dragOver, setDragOver] = useState(false);
  // Files attached on Home travel with the expand button.
  useEffect(() => {
    if (!embedded) return;
    try {
      const raw = sessionStorage.getItem('lyra:composer:files');
      if (raw) { const f = JSON.parse(raw); if (Array.isArray(f)) setPendingFiles(f); sessionStorage.removeItem('lyra:composer:files'); }
    } catch (e) { /* nothing to restore */ }
  }, [embedded]);
  const [isMobileView,    setIsMobileView]    = useState(typeof window !== "undefined" ? window.innerWidth <= 768 : false);
  // Advanced runtime/context information is progressive disclosure: hidden until requested.
  const [showContext,     setShowContext]     = useState(false);

  // ── Inline "Connect integration" (see INTEGRATION_META above) ─────────────
  // { id, retryText } while a modal-based connect is open; null otherwise.
  const [connectingIntegration, setConnectingIntegration] = useState(null);

  // ── Chain progress state ──────────────────────────────────────────────────
  // activeStepIdx: null = idle, 0..n = currently executing step, -1 = finished
  // stepStatuses: "waiting" | "running" | "done" | "error" per step
  const [activeStepIdx, setActiveStepIdx] = useState(null);
  const [stepStatuses,  setStepStatuses]  = useState([]);

  const scrollRef     = useRef(null);
  const inputRef      = useRef(null);
  const pickerRef     = useRef(null);
  const fileInputRef  = useRef(null);
  const lastPromptRef = useRef("");
  const sendRef        = useRef(null);

  const isAgent          = targetType === "agent";
  const isChain          = targetType === "chain";
  const effectiveModelId = localModelId || selectedModelId || null;
  const effectiveMode    = localModelId ? "manual" : selectedMode;
  const effectiveKey     = effectiveModelId ? (customApiKeys?.[effectiveModelId] || undefined) : undefined;
  // The picker lists the models the user added on the Models page. Built-ins stay reachable
  // ("Show all") and are shown automatically until a user-added model has a saved key, so the
  // only working model can never disappear.
  const [showAllModels, setShowAllModels] = useState(false);
  const hasWorkingUserModel = enabledModels.some(m => m.isUserAdded && customApiKeys?.[m.baseSystemId]);
  const curatedModels = hasWorkingUserModel && !showAllModels;
  const pickerModels = curatedModels ? enabledModels.filter(m => m.isUserAdded || m.id === effectiveModelId) : enabledModels;
  const activeModelObj   = effectiveModelId ? enabledModels.find(m => m.id === effectiveModelId) : null;
  const modelLabel       = localModelId ? (activeModelObj?.name || localModelId) : selectedMode === "auto" ? "⚡ Auto" : (activeModelObj?.name || "Select model");
  const isEmailAgent     = isAgent && (target?.name?.toLowerCase().includes("email") || target?.id === "email");

  // Minimized state: when user clicks outside, slide drawer off-screen (global FAB takes over)
  const [minimized, setMinimized] = useState(false);
  // isClosing: triggers slide-out animation before unmounting
  const [isClosing, setIsClosing] = useState(false);

  // Persisted session storage key
  const STORAGE_KEY = 'lyra:runDrawerState:v1';

  // Show the chain progress strip while a chain run is in flight or just finished
  const showChainStrip = isChain && activeStepIdx !== null;

  // Load MCP servers attached to this agent
  useEffect(() => {
    const attachedIds = target?.mcpServerIds;
    if (!attachedIds || attachedIds.length === 0) { setMcpServers([]); return; }
    mcpAPI.listServers()
      .then(r => {
        const idSet = new Set(attachedIds.map(id => id.toString()));
        setMcpServers((r.servers || []).filter(s => idSet.has(s._id?.toString())));
      })
      .catch(() => {});
  }, [target?._id, target?.mcpServerIds, target?.id]);

  // Track mobile viewport for responsive drawer behavior
  useEffect(() => {
    const onResize = () => setIsMobileView(window.innerWidth <= 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Keep advanced runtime information closed when the viewport changes to desktop.
  useEffect(() => {
    if (!isMobileView) setShowContext(false);
  }, [isMobileView]);

  // Reset progressive context panel when switching targets.
  useEffect(() => {
    setShowContext(false);
  }, [target?._id, target?.id]);

  // Reset on target change and preselect agent when provided
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  // For chains/workflows opened via RunDrawer, we also track the chain target
  const [selectedChainId, setSelectedChainId] = useState(null);
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const dropdownWrapperRef = useRef(null);
  const agentPos = useAnchoredAbove(showAgentDropdown, dropdownWrapperRef, { width: 400, maxHeight: 340 });
  const modelPos = useAnchoredAbove(showModelPicker, pickerRef, { width: 300, maxHeight: 420 });
  const [dropdownSearch, setDropdownSearch] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);

  // Mentions & tools (like Conversations compose bar)
  const [agentWorkflowList, setAgentWorkflowList] = useState([]);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [mentionAnchor, setMentionAnchor] = useState(null);
  // Chips: selected agents/workflows shown as pills above the textarea
  const [mentionedChips, setMentionedChips] = useState([]);

  // Determine the currently active agent (either the explicit target, or the selected one from the dropdown)
  const activeAgent = target || agents.find(a => (a._id || a.id) === selectedAgentId) || null;
  const displayTarget = activeAgent || target || {};

  // Eagerly load agents + workflows on mount so @ suggestions are instant
  useEffect(() => {
    (async () => {
      try {
        const [agentsRes, chainsRes] = await Promise.allSettled([customAgentAPI.list(), customAgentAPI.listChains()]);
        const agentsList = (agentsRes.status === 'fulfilled' ? (agentsRes.value?.agents || agentsRes.value || []) : []);
        const chainsList = (chainsRes.status === 'fulfilled' ? (chainsRes.value?.chains || chainsRes.value || []) : []);
        const merged = [
          ...agentsList.map(a => ({ _id: a._id || a.id, name: a.name, description: a.description, icon: a.icon, _type: 'agent' })),
          ...chainsList.map(c => ({ _id: c._id || c.id, name: c.name, description: c.description, _type: 'workflow', steps: c.steps, source: c.source, canvasData: c.canvasData })),
        ];
        setAgentWorkflowList(merged);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  // When a target changes, prefer selecting it but do NOT clear the running session
  // so that the conversation and run state persist across route changes.
  useEffect(() => {
    const selId = target?._id || target?.id || null;
    if (selId) {
      setSelectedAgentId(selId);
      setShowAgentDropdown(false);
    }
    setMinimized(false);
    setTimeout(() => inputRef.current?.focus(), 50);
    // NOTE: Do NOT dispatch run-open/run-close here — Layout controls open/close events.
  }, [target?._id, target?.id]);

  // Load persisted session on mount — skipped in page mode: "New Task" is a
  // fresh start every visit, not a resume point, and this sessionStorage key
  // is shared globally with every floating-drawer instance in the app, so
  // restoring it here would silently inherit whatever agent/chat was last
  // active anywhere else.
  useEffect(() => {
    if (embedded) return;
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const s = JSON.parse(raw);
        if (s) {
          if (s.prompt) setPrompt(s.prompt);
          if (Array.isArray(s.history)) setHistory(s.history);
          if (Array.isArray(s.runTrace)) setRunTrace(s.runTrace);
          if (Array.isArray(s.rawPayloads)) setRawPayloads(s.rawPayloads);
          if (s.lastResult) setLastResult(s.lastResult);
          if (s.minimized) setMinimized(!!s.minimized);
          if (s.selectedAgentId) setSelectedAgentId(s.selectedAgentId);
          if (s.activeTab) setActiveTab(s.activeTab);
          if (s.activeStepIdx !== undefined) setActiveStepIdx(s.activeStepIdx);
          if (Array.isArray(s.stepStatuses)) setStepStatuses(s.stepStatuses);
          if (Array.isArray(s.runMemories)) setRunMemories(s.runMemories);
          if (Array.isArray(s.pendingFiles)) setPendingFiles(s.pendingFiles);
        }
      }
    } catch (e) {
      // ignore parse errors
    }
  }, [embedded]);

  // Persist session on key state changes — skipped in page mode (see above);
  // also avoids a page-mode instance overwriting the floating drawer's
  // legitimate saved session with its own unrelated state.
  useEffect(() => {
    if (embedded) return;
    const payload = {
      prompt,
      history,
      runTrace,
      rawPayloads,
      lastResult,
      minimized,
      selectedAgentId,
      activeTab,
      activeStepIdx,
      stepStatuses,
      runMemories,
      pendingFiles,
    };
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch (e) {}
  }, [embedded, prompt, history, runTrace, rawPayloads, lastResult, minimized, selectedAgentId, activeTab, activeStepIdx, stepStatuses, runMemories, pendingFiles]);

  // ── Saved-chat persistence (page mode only) ───────────────────────────────
  // The floating drawer keeps its sessionStorage-only behavior above,
  // unchanged. Page mode ("New Task") additionally saves each chat as a
  // ChatSession in the DB — the same model/routes the Conversations page
  // uses — identified by a `?session=<id>` query param so a sibling history
  // rail (AIAssistantPage.tsx's ChatHistoryRail) and browser back/forward
  // both work naturally. Execution itself (customAgentAPI.execute below) is
  // completely unaffected — this is pure persistence layered on top.
  const [dbSessionId, setDbSessionId] = useState(null);
  // Mirrors dbSessionId synchronously — state updates from inside an async
  // save don't become visible to that same closure until the next render,
  // so a queued retry (see persistHistory below) needs this to know
  // immediately whether the session it just created should now be updated
  // instead of created again.
  const dbSessionIdRef = useRef(null);
  const setDbSessionIdBoth = useCallback((id) => { dbSessionIdRef.current = id; setDbSessionId(id); }, []);
  const skipNextAutosaveRef = useRef(false);
  const historyRef = useRef(history);
  useEffect(() => { historyRef.current = history; }, [history]);
  const urlSessionId = embedded ? searchParams.get('session') : null;

  // Load a session's history when the URL's ?session= changes (covers both
  // the initial page load and clicking a different chat in the rail, which
  // updates the query param without unmounting this component).
  useEffect(() => {
    if (!embedded) return;
    if (!urlSessionId) {
      // Rail's "New chat" (or a fresh /ai-assistant visit) clears the param.
      if (dbSessionIdRef.current) { setDbSessionIdBoth(null); setHistory([]); }
      return;
    }
    if (urlSessionId === dbSessionIdRef.current) return;
    (async () => {
      try {
        const res = await chatSessionAPI.get(urlSessionId);
        const msgs = (res?.session?.messages || []).map(m => ({
          role: m.role,
          text: m.content,
          ts: m.ts ? new Date(m.ts).getTime() : Date.now(),
          modelName: m.modelName,
          durationMs: m.durationMs,
          ...(m.meta || {}),
        }));
        skipNextAutosaveRef.current = true;
        setHistory(msgs);
        setDbSessionIdBoth(urlSessionId);
      } catch (_) {
        // Session gone or not ours — fall back to a blank chat rather than looping.
        setDbSessionIdBoth(null);
      }
    })();
  }, [embedded, urlSessionId]);

  // Auto-save whenever history changes in page mode. Deliberately NOT
  // debounced with a cancellable setTimeout — a quick navigation right
  // after the last message would cancel a pending timer and lose that turn
  // entirely; firing immediately means the request is already in flight
  // (and completes) even if the component unmounts right after. Saves are
  // serialized through saveInFlightRef/saveAgainRef rather than fired in
  // parallel — history changes twice per turn (user message, then the
  // assistant reply a few seconds later), and two overlapping saves before
  // either resolves would both see no dbSessionId yet and create two
  // separate sessions for one conversation.
  const saveInFlightRef = useRef(false);
  const saveAgainRef = useRef(false);
  // Read the latest onSessionSaved/setSearchParams via refs, updated every
  // render, rather than depending on them directly in persistHistory's own
  // useCallback deps. AIAssistantPage.tsx previously passed a fresh inline
  // `onSessionSaved` function on every render — since that identity change
  // propagated through persistHistory into the autosave effect's deps below,
  // every render re-created the effect, which called persistHistory() again,
  // which called onSessionSaved() (bumping the parent's refresh key) and
  // triggered another render — an infinite loop hammering the save/list/
  // folders endpoints. Keeping persistHistory itself fully stable (empty
  // dep array) makes this resilient regardless of whether a caller memoizes
  // what it passes in.
  const onSessionSavedRef = useRef(onSessionSaved);
  useEffect(() => { onSessionSavedRef.current = onSessionSaved; });
  const setSearchParamsRef = useRef(setSearchParams);
  useEffect(() => { setSearchParamsRef.current = setSearchParams; });
  const persistHistory = useCallback(async () => {
    if (saveInFlightRef.current) { saveAgainRef.current = true; return; }
    const snapshot = historyRef.current;
    if (!snapshot || snapshot.length === 0) return;
    saveInFlightRef.current = true;
    try {
      const mapped = snapshot.map((h) => {
        const { role, text, ts, modelName, durationMs, ...rest } = h;
        if (rest.attachments) rest.attachments = rest.attachments.map(({ id, name, contentType, size }) => ({ id, name, contentType, size }));   // the File itself is only for this tab
        const meta = Object.keys(rest).length ? rest : undefined;
        return { role, content: text ?? '', ts: ts ? new Date(ts) : new Date(), modelName, durationMs, meta };
      });
      if (!dbSessionIdRef.current) {
        const firstUser = snapshot.find((h) => h.role === 'user');
        const title = (firstUser?.text || 'New Chat').slice(0, 60);
        const created = await chatSessionAPI.create({ title, messages: mapped });
        const newId = created?.session?._id;
        if (newId) {
          setDbSessionIdBoth(newId);
          setSearchParamsRef.current((prev) => {
            const next = new URLSearchParams(prev);
            next.set('session', newId);
            return next;
          }, { replace: true });
        }
      } else {
        await chatSessionAPI.update(dbSessionIdRef.current, { messages: mapped });
      }
      if (onSessionSavedRef.current) onSessionSavedRef.current();
    } catch (_) {
      // Non-fatal — a failed save never interrupts the chat itself.
    } finally {
      saveInFlightRef.current = false;
      if (saveAgainRef.current) {
        saveAgainRef.current = false;
        persistHistory();
      }
    }
  }, [setDbSessionIdBoth]);

  useEffect(() => {
    if (!embedded) return;
    if (!history || history.length === 0) return;
    if (skipNextAutosaveRef.current) { skipNextAutosaveRef.current = false; return; }
    if (!autosave) return;
    persistHistory();
  }, [embedded, history, persistHistory, autosave]);

  // A host's "New chat": clear the conversation even when it was never saved.
  const lastResetKey = useRef(resetKey);
  useEffect(() => {
    if (!embedded || resetKey === lastResetKey.current) return;
    lastResetKey.current = resetKey;
    setDbSessionIdBoth(null);
    setHistory([]);
    setPendingFiles([]);
  }, [embedded, resetKey, setDbSessionIdBoth]);

  // Detaches the current chat from its saved ChatSession (used by "Clear
  // chat" / "New task") — without this, typing after a clear would silently
  // overwrite the old saved session's messages via the update-by-replace
  // autosave above, instead of starting a fresh one.
  const clearSavedSession = useCallback(() => {
    if (!embedded) return;
    setDbSessionIdBoth(null);
    if (searchParams.has('session')) {
      const next = new URLSearchParams(searchParams);
      next.delete('session');
      setSearchParams(next, { replace: true });
    }
  }, [embedded, searchParams, setSearchParams, setDbSessionIdBoth]);

  // Emit minimize/restore events so Layout can show a single global FAB
  useEffect(() => {
    try {
      if (minimized) window.dispatchEvent(new CustomEvent('lyra:run-minimized'));
      else window.dispatchEvent(new CustomEvent('lyra:run-restore'));
    } catch (e) {}
  }, [minimized]);

  // Listen for restore requests from the global FAB
  useEffect(() => {
    const onRestore = () => setMinimized(false);
    window.addEventListener('lyra:run-restore', onRestore);
    return () => window.removeEventListener('lyra:run-restore', onRestore);
  }, []);

  // Listen for requests to set the selected agent/chain (when Layout wants to restore and select)
  // Also handles prepopulateText — set the prompt text so user can immediately hit send
  useEffect(() => {
    const onSetTarget = (e) => {
      const agentId = e?.detail?.agentId || null;
      const chainId = e?.detail?.chainId || null;
      const prepopulateText = e?.detail?.prepopulateText || null;
      const autoSend = !!e?.detail?.autoSend;
      if (agentId) {
        setSelectedAgentId(agentId);
        setShowAgentDropdown(false);
      }
      if (chainId) {
        setSelectedChainId(chainId);
      }
      if (prepopulateText) {
        setPrompt(prepopulateText);
        // Also parse any @mentions in the prepopulated text and turn them into chips
        // We do this after a short delay to let agentWorkflowList load
        setTimeout(() => {
          setAgentWorkflowList(prev => {
            if (prev.length > 0) {
              const chips = [];
              const workflowRe = /@workflow:([\w\s-]+?)(?=\s|@|$)/gi;
              const agentRe    = /@([\w\s-]+?)(?=\s|@|$)/gi;
              let m;
              const text = prepopulateText;
              while ((m = workflowRe.exec(text)) !== null) {
                const name = m[1].trim();
                const match = prev.find(x => x._type === 'workflow' && (x.name || '').toLowerCase() === name.toLowerCase());
                if (match && !chips.some(c => c._id === match._id)) chips.push(match);
              }
              const textNoW = text.replace(workflowRe, '');
              while ((m = agentRe.exec(textNoW)) !== null) {
                const name = m[1].trim();
                if (!name) continue;
                const match = prev.find(x => x._type === 'agent' && (x.name || '').toLowerCase() === name.toLowerCase());
                if (match && !chips.some(c => c._id === match._id)) chips.push(match);
              }
              if (chips.length > 0) { setMentionedChips(chips); setPrompt(''); }
            }
            return prev;
          });
        }, 300);
      }
      setMinimized(false);
      setTimeout(() => inputRef.current?.focus(), 80);
      if (autoSend && prepopulateText) {
        setTimeout(() => { sendRef.current && sendRef.current(); }, 360);
      }
    };
    window.addEventListener('lyra:set-run-target', onSetTarget);
    return () => window.removeEventListener('lyra:set-run-target', onSetTarget);
  }, []);

  // Detect @mention as user types (suggest agents + workflows)
  useEffect(() => {
    const at = prompt.lastIndexOf('@');
    if (at === -1) { setMentionSuggestions([]); return; }
    // Only trigger if @ is at start or preceded by whitespace
    const before = prompt[at - 1];
    if (before && before !== ' ' && before !== '\n') { setMentionSuggestions([]); return; }
    const q = prompt.slice(at + 1);
    // Close if space encountered after mention (unless still typing)
    if (q.includes(' ') && q.trim().length > 0) { setMentionSuggestions([]); return; }

    // Use eagerly-loaded list; fall back to inline fetch if list not ready yet
    const source = agentWorkflowList.length > 0
      ? agentWorkflowList
      : [
          ...(agents || []).map(a => ({ _id: a._id || a.id, name: a.name, description: a.description, icon: a.icon, _type: 'agent' })),
        ];
    const filtered = source.filter(item => (item.name || '').toLowerCase().startsWith(q.toLowerCase())).slice(0, 6);
    setMentionSuggestions(filtered);
    setMentionAnchor({ bottom: 60 });
  }, [prompt, agentWorkflowList, agents]);

  // Auto-scroll chat
  // Scroll only the message list itself (never the page: scrollIntoView would move the page when the chat is inline on Home).
  useEffect(() => {
    if (!history.length && !running) return;
    const list = scrollRef.current?.closest(".rd-chat-scroll");
    if (list) list.scrollTo({ top: list.scrollHeight, behavior: "smooth" });
  }, [history, running]);

  // Close model picker on outside click
  useEffect(() => {
    if (!showModelPicker) return;
    const h = e => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowModelPicker(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [showModelPicker]);

  // Close agent dropdown on outside click
  useEffect(() => {
    if (!showAgentDropdown) return;
    const h = e => {
      if (dropdownWrapperRef.current && !dropdownWrapperRef.current.contains(e.target)) setShowAgentDropdown(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showAgentDropdown]);

  

  // ── Build debug trace ─────────────────────────────────────────────────────
  const buildTrace = useCallback((res, text, userTs) => {
    const toolCalls   = res.toolCalls || res.steps || [];
    const toolMs      = toolCalls.reduce((a, tc) => a + (tc.latencyMs || 0), 0);
    const reasoningMs = res.durationMs != null ? Math.max(0, res.durationMs - toolMs) : undefined;
    const steps       = [];

    steps.push({ id: "user_input",      type: "user_input",      label: "User Input",      input: null, output: { message: text }, status: "success", ts: userTs });
    steps.push({ id: "agent_reasoning", type: "agent_reasoning", label: "Agent Reasoning", input: { model: res.modelName || effectiveModelId || "auto" }, output: res.thinking || { status: `Processed with ${res.modelName || "AI model"}` }, latencyMs: reasoningMs, tokens: res.usage, status: "success", ts: userTs + 10 });

    toolCalls.forEach((tc, i) => {
      steps.push({ id: `tool_call_${i}`, type: "tool_call",     label: `Tool: ${tc.name || `Tool ${i + 1}`}`,     input: tc.input || null,  output: null,                latencyMs: tc.latencyMs, status: tc.error ? "error" : "success", ts: userTs + 50 * (i + 1) });
      steps.push({ id: `tool_resp_${i}`, type: "tool_response", label: `Response: ${tc.name || `Tool ${i + 1}`}`, input: null, output: tc.result ?? tc.output ?? null, status: tc.error ? "error" : "success", ts: userTs + 50 * (i + 1) + (tc.latencyMs || 0) });
    });

    steps.push({ id: "final_output", type: "final_output", label: "Final Output", input: null, output: { result: typeof res.result === "string" ? res.result : JSON.stringify(res.result) }, latencyMs: res.durationMs, tokens: res.usage, status: "success", ts: Date.now() });
    return steps;
  }, [effectiveModelId]);

  // Unique "agent · tool" pairs from a run's MCP tool calls (skillId = "{server}__{tool}"), for the reply badge.
  // Proposed actions waiting for the user's yes/no (e.g. an email to send): shown as cards under the reply.
  const approvalsOf = (res) => (res?.toolCalls || [])
    .map((tc) => tc && tc.result)
    .filter((r) => r && r.status === 'awaiting_user_approval' && r.approvalId)
    .map((r) => ({ approvalId: r.approvalId, skill: r.skill, preview: r.preview }));
  const mcpCallsOf = (res) => {
    const seen = new Set();
    (res?.toolCalls || []).forEach(tc => {
      if (!tc.skillId || !tc.skillId.includes("__")) return;
      const i = tc.skillId.indexOf("__");
      seen.add(`${tc.skillId.slice(0, i)} · ${tc.skillId.slice(i + 2)}`);
    });
    return [...seen];
  };

  // ── Execute agent ─────────────────────────────────────────────────────────
  const executeAgent = useCallback(async (text, agentId) => {
    const userTs     = Date.now();
    lastPromptRef.current = text;
    const runId = agentId || (activeAgent?._id || activeAgent?.id) || (target?._id || target?.id);
    const histPayload = history.map(m => ({ role: m.role, content: m.text }));
    const reqPayload  = { model: effectiveModelId || "auto", agentId: runId, messages: [...histPayload, { role: "user", content: text }], mode: effectiveMode };
    try {
      // Emit global run-start event so top-level UI (status strip) can react
      try { window.dispatchEvent(new CustomEvent('lyra:run-start', { detail: { targetType: 'agent', id: runId } })); } catch (e) {}

      let res;

      // ── Catalogue agents: no DB record — run via general AI endpoint ──────
      const isCatalogueAgent = runId && String(runId).startsWith('catalogue:');
      if (isCatalogueAgent) {
        // Resolve the synthetic agent from the agents prop or activeAgent
        const catalogueAgent = agents.find(a => (a._id || a.id) === runId) || activeAgent || {};
        const sysPrompt = catalogueAgent.systemPrompt || '';
        // Build conversation context as a single input string
        const historyContext = histPayload.length > 0
          ? histPayload.map(m => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`).join('\n') + '\nUser: ' + text
          : text;
        res = await agentAPI.run({
          input:        historyContext,
          taskId:       'custom',
          customSystem: sysPrompt,
          mode:         effectiveMode,
          modelId:      effectiveModelId || undefined,
          customApiKey: effectiveKey,
        });
      } else {
        const execBody = { prompt: text, history: histPayload, mode: effectiveMode, modelId: effectiveModelId || undefined, customApiKey: effectiveKey, ...(domainAgentIds.length ? { domainAgentIds } : {}), externalAgentIds,
          ...(picks.skillIds.length ? { skillIds: picks.skillIds } : {}), ...(picks.spaceRefs.length ? { knowledgeSpaceIds: picks.spaceRefs } : {}), ...(attachRef.current.length ? { attachmentIds: attachRef.current } : {}) };
        res = await customAgentAPI.execute(runId, execBody);
      }

      const resultText = typeof res.result === "object" ? JSON.stringify(res.result, null, 2) : (res.result || res.output || "");
      const mcpToolCount = (res.toolCalls || []).filter(tc => tc.skillId?.includes("__")).length;
      setHistory(h => [...h, {
        role: "assistant", text: resultText, ts: Date.now(), durationMs: res.durationMs, modelName: res.modelName, mcpToolCount, mcpCalls: mcpCallsOf(res), tasks: tasksOf(res), agentRunId: res.agentRunId || null, approvals: approvalsOf(res),
        needsIntegration: res.needsIntegration || null,
        suggestions: res.suggestions || null,
        citations: res.knowledgeCitations || [],
        knowledgeNote: res.knowledgeNote || null,
      }]);
      setLastResult(res);
      setRunTrace(buildTrace(res, text, userTs));
      setRawPayloads([
        { id: "llm_req",  label: "LLM Request",  payload: reqPayload },
        { id: "llm_resp", label: "LLM Response", payload: res },
        ...(res.toolCalls || []).map((tc, i) => ({ id: `tc_${i}`, label: `Tool: ${tc.name || i}`, payload: tc })),
      ]);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Agent failed";
      const mf = reportRunError(msg);
      setHistory(h => [...h, { role: "assistant", text: mf ? mf.chatText : `⚠️ ${redactSecrets(msg)}`, ts: Date.now(), error: true, modelFailure: mf ? { ...mf, raw: msg } : null }]);
      setRunTrace([
        { id: "user_input",   type: "user_input",   label: "User Input",   input: null, output: { message: text }, status: "success", ts: userTs },
        { id: "final_output", type: "final_output", label: "Final Output", input: null, output: { error: msg },    status: "error",   ts: Date.now() },
      ]);
    }
    finally {
      try { window.dispatchEvent(new CustomEvent('lyra:run-end', { detail: { targetType: 'agent', id: runId } })); } catch (e) {}
    }
  }, [target, activeAgent, agents, history, effectiveModelId, effectiveMode, effectiveKey, buildTrace, domainAgentIds.join(','), externalAgentIds.join(','), picks.skillIds.join(','), JSON.stringify(picks.spaceRefs)]);

  // ── Execute with no specific agent picked ───────────────────────────────────
  // Same general-purpose path already used for onboarding "catalogue" agents
  // (agentAPI.run with a custom system prompt, no DB agent record required) —
  // lets a fresh "New Task" run immediately without forcing an @mention first.
  const executeGeneral = useCallback(async (text) => {
    const userTs = Date.now();
    try { window.dispatchEvent(new CustomEvent('lyra:run-start', { detail: { targetType: 'agent', id: 'general' } })); } catch (e) {}
    try {
      const histPayload = history.map(m => ({ role: m.role, content: m.text }));
      const historyContext = histPayload.length > 0
        ? histPayload.map(m => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`).join('\n') + '\nUser: ' + text
        : text;
      const res = await agentAPI.run({
        input: historyContext,
        taskId: 'custom',
        customSystem: 'You are Lyra, a helpful AI assistant that plans tasks and can delegate to specialized agents. Be concise and actionable.',
        mode: effectiveMode,
        modelId: effectiveModelId || undefined,
        customApiKey: effectiveKey,
        // Let the server route this through the user's connected domain agents (MCP, e.g. Workforce) when they have any.
        useDomainAgents: true,
        ...(domainAgentIds.length ? { domainAgentIds } : {}),
        externalAgentIds,
        ...(picks.skillIds.length ? { skillIds: picks.skillIds } : {}),
        ...(picks.spaceRefs.length ? { knowledgeSpaceIds: picks.spaceRefs } : {}),
        ...(attachRef.current.length ? { attachmentIds: attachRef.current } : {}),
      });
      const resultText = typeof res.result === "object" ? JSON.stringify(res.result, null, 2) : (res.result || res.output || "");
      const mcpToolCount = (res.toolCalls || []).filter(tc => tc.skillId?.includes("__")).length;
      setHistory(h => [...h, { role: "assistant", text: resultText, ts: Date.now(), durationMs: res.durationMs, modelName: res.modelName, mcpToolCount, mcpCalls: mcpCallsOf(res), tasks: tasksOf(res), agentRunId: res.agentRunId || null, approvals: approvalsOf(res), citations: res.knowledgeCitations || [], knowledgeNote: res.knowledgeNote || null }]);
      setLastResult(res);
      setRunTrace(buildTrace(res, text, userTs));
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Run failed";
      const mf = reportRunError(msg);
      setHistory(h => [...h, { role: "assistant", text: mf ? mf.chatText : `⚠️ ${redactSecrets(msg)}`, ts: Date.now(), error: true, modelFailure: mf ? { ...mf, raw: msg } : null }]);
    } finally {
      try { window.dispatchEvent(new CustomEvent('lyra:run-end', { detail: { targetType: 'agent', id: 'general' } })); } catch (e) {}
    }
  }, [history, effectiveModelId, effectiveMode, effectiveKey, buildTrace, domainAgentIds.join(','), externalAgentIds.join(','), picks.skillIds.join(','), JSON.stringify(picks.spaceRefs)]);

  // ── Execute chain ─────────────────────────────────────────────────────────
  // chainTarget can be a chain/workflow object passed explicitly (for parallel runs)
  const executeChain = useCallback(async (text, chainTarget) => {
    const resolvedTarget = chainTarget || activeAgent || target || {};
    const steps       = (resolvedTarget?.steps) || [];
    const stepsResult = [];
    let   lastOutput  = text;

    // Initialise per-step status, show the strip
    setStepStatuses(steps.map(() => "waiting"));
    setActiveStepIdx(0);

    const chainId = resolvedTarget?._id || resolvedTarget?.id;

    try {
      // Emit global run-start for chain
      try { window.dispatchEvent(new CustomEvent('lyra:run-start', { detail: { targetType: 'chain', id: chainId } })); } catch (e) {}
      for (let i = 0; i < steps.length; i++) {
        // Mark this step as running
        setActiveStepIdx(i);
        setStepStatuses(prev => prev.map((s, j) => j === i ? "running" : s));

        // Emit per-step event
        try { window.dispatchEvent(new CustomEvent('lyra:run-step', { detail: { chainId, stepIndex: i } })); } catch (e) {}

        const step       = steps[i];
        // Resolve agent from local agents prop OR from agentWorkflowList
        const agentEntry = agents.find(a => a._id === step.agentId || a.id === step.agentId);
        const agentId    = agentEntry?._id || step.agentId;

        try {
          const stepData   = await customAgentAPI.execute(agentId, { prompt: `Task: ${step.label || `Step ${i + 1}`}\n\nInput:\n${lastOutput}`, mode: effectiveMode, modelId: effectiveModelId || undefined, customApiKey: effectiveKey });
          const resultText = typeof stepData.result === "string" ? stepData.result : JSON.stringify(stepData.result, null, 2);
          lastOutput = resultText;
          stepsResult.push({ step: i + 1, agent: agentEntry?.name || step.label || `Step ${i + 1}`, result: resultText, modelUsed: stepData.modelName });

          // Mark done
          setStepStatuses(prev => prev.map((s, j) => j === i ? "done" : s));
        } catch (stepErr) {
          // Mark this specific step as error, then rethrow so outer catch handles the rest
          setStepStatuses(prev => prev.map((s, j) => j === i ? "error" : s));
          throw stepErr;
        }
      }

      const chainResult = { chainId, chainName: (resolvedTarget.name || ''), steps: stepsResult, finalOutput: lastOutput };
      setLastResult(chainResult);
      const summary = stepsResult.map((s, i) => `Step ${i + 1} — ${s.agent}\n${s.result}`).join("\n\n---\n\n");
      setHistory(h => [...h, { role: "assistant", text: summary, ts: Date.now(), isChain: true, chainData: chainResult }]);
      toast.success(`${resolvedTarget.name || 'Workflow'} completed`);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Chain failed";
      const mf = reportRunError(msg);
      setHistory(h => [...h, { role: "assistant", text: mf ? mf.chatText : `⚠️ ${redactSecrets(msg)}`, ts: Date.now(), error: true, modelFailure: mf ? { ...mf, raw: msg } : null }]);
    } finally {
      // Signal finished — strip persists so user can see final state
      setActiveStepIdx(-1);
      try { window.dispatchEvent(new CustomEvent('lyra:run-end', { detail: { targetType: 'chain', id: chainId } })); } catch (e) {}
    }
  }, [target, activeAgent, agents, effectiveModelId, effectiveMode, effectiveKey]);

  // ── Execute canvas flow ───────────────────────────────────────────────────
  const executeFlow = useCallback(async (text) => {
    // Collect ordered agent nodes for the progress strip
    const agentNodes = ((activeAgent?.canvasData?.nodes) || (target?.canvasData?.nodes) || []).filter(n => n.agid);
    if (agentNodes.length > 0) {
      setStepStatuses(agentNodes.map((_, i) => i === 0 ? "running" : "waiting"));
      setActiveStepIdx(0);
    }
    try {
      try { window.dispatchEvent(new CustomEvent('lyra:run-start', { detail: { targetType: 'flow', id: (activeAgent?._id || target?._id) } })); } catch (e) {}
      const res = await customAgentAPI.runFlow(
        (activeAgent?._id || target?._id),
        (activeAgent?.canvasData?.nodes || target?.canvasData?.nodes || []),
        (activeAgent?.canvasData?.connections || target?.canvasData?.connections || []),
        text || "Run flow",
        (activeAgent?.name || target?.name || ''),
        effectiveModelId || undefined
      );
      const nodeResults = res?.nodeResults || [];
      // Animate through results sequentially so user sees each node complete
      nodeResults.forEach((r, idx) => {
        setTimeout(() => {
          const nodeIdx = agentNodes.findIndex(n => n.id === r.nodeId);
          const targetIdx = nodeIdx !== -1 ? nodeIdx : idx;
          setActiveStepIdx(targetIdx);
          setStepStatuses(prev => prev.map((s, j) => {
            if (j < targetIdx) return "done";
            if (j === targetIdx) return r.error ? "error" : "done";
            return s;
          }));
        }, idx * 400);
      });
      setTimeout(() => {
        setActiveStepIdx(-1);
        try { window.dispatchEvent(new CustomEvent('lyra:run-end', { detail: { targetType: 'flow', id: (activeAgent?._id || target?._id) } })); } catch (e) {}
      }, (nodeResults.length || 1) * 400 + 300);
      const summary = nodeResults.length
        ? nodeResults.map(nr =>
            `${nr.error ? "❌" : "✅"} **${nr.name}**\n${nr.error ? `Error: ${nr.error}` : (nr.output || "(no output)")}`
          ).join("\n\n")
        : res?.output || "✅ Flow completed";
      setHistory(h => [...h, { role: "assistant", text: summary, ts: Date.now(), isFlow: true, nodeResults }]);
      setLastResult(res);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Flow failed";
      const mf = reportRunError(msg);
      setHistory(h => [...h, { role: "assistant", text: mf ? mf.chatText : `⚠️ ${redactSecrets(msg)}`, ts: Date.now(), error: true, modelFailure: mf ? { ...mf, raw: msg } : null }]);
      setActiveStepIdx(-1);
      try { window.dispatchEvent(new CustomEvent('lyra:run-end', { detail: { targetType: 'flow', id: (activeAgent?._id || target?._id) } })); } catch (e) {}
    }
  }, [target, activeAgent, setStepStatuses, setActiveStepIdx]);

  // ── Chip helpers (add / remove agents or workflows as pill chips) ─────────
  const addChip = useCallback((item) => {
    if (!item) return;
    setMentionedChips(prev => prev.some(c => c._id === (item._id || item.id)) ? prev : [...prev, item]);
    if (item._type === 'agent') setSelectedAgentId(item._id || item.id);
    else if (item._type === 'workflow') setSelectedChainId(item._id || item.id);
    setMentionSuggestions([]);
  }, []);

  const removeChip = useCallback((chipId) => {
    setMentionedChips(prev => prev.filter(c => c._id !== chipId && c.id !== chipId));
  }, []);

  // ── Parse @ mentions from prompt text ─────────────────────────────────────
  // Returns array of resolved targets: { _id, name, _type, ...chain fields }
  // Also includes any chips already added via the chip row
  const parseMentions = useCallback((text) => {
    const found = [...mentionedChips]; // always include chips
    // Match @workflow:Name or @Name tokens in the text
    const workflowRe = /@workflow:([\w\s-]+?)(?=\s|@|$)/gi;
    const agentRe    = /@([\w\s-]+?)(?=\s|@|$)/gi;
    let m;
    while ((m = workflowRe.exec(text)) !== null) {
      const name = m[1].trim();
      const match = agentWorkflowList.find(x => x._type === 'workflow' && (x.name || '').toLowerCase() === name.toLowerCase());
      if (match && !found.some(f => f._id === match._id)) found.push(match);
    }
    const textNoWorkflow = text.replace(workflowRe, '');
    while ((m = agentRe.exec(textNoWorkflow)) !== null) {
      const name = m[1].trim();
      if (!name) continue;
      const match = agentWorkflowList.find(x => x._type === 'agent' && (x.name || '').toLowerCase() === name.toLowerCase());
      if (match && !found.some(f => f._id === match._id)) found.push(match);
    }
    return found;
  }, [agentWorkflowList, mentionedChips]);

  // ── Send ──────────────────────────────────────────────────────────────────
  // `retryText` lets the "connect an integration" flow (see connectIntegration
  // below) resend the exact request that triggered a [[NEEDS_INTEGRATION]]
  // reply once the user connects — reusing this function's own dispatch
  // logic (mentions/chips/agent/chain/flow) rather than duplicating it.
  const send = useCallback(async (retryArg) => {
    // onClick passes the click event; only a string is a retry of an earlier message
    const retryText = typeof retryArg === 'string' ? retryArg : undefined;
    const text = (retryText ?? prompt).trim();
    const hasChips = !retryText && mentionedChips.length > 0;
    // Allow send if there's text OR if chips are selected (chips alone can trigger a run)
    if ((!text && !hasChips) || running) return;
    if (prefsLoaded && !hasApiKey) { setKeyPromptOpen(true); return; }
    const displayText = text || (hasChips ? `Run ${mentionedChips.map(c => c.name).join(', ')}` : '');
    setPrompt("");
    setHistory(h => [...h, { role: "user", text: displayText, ts: Date.now(),
      ...(!retryText && pendingFiles.length ? { attachments: pendingFiles.map(({ id, name, contentType, size, blob }) => ({ id, name, contentType, size, blob })) } : {}) }]);
    setRunning(true);

    // Reset chain strip for a fresh run
    if (isChain) {
      setActiveStepIdx(null);
      setStepStatuses([]);
    }

    // Build file suffix once
    let fileSuffix = '';
    if (pendingFiles.length > 0) {
      const textFiles = pendingFiles.filter(f => f.content);
      const binFiles  = pendingFiles.filter(f => !f.content);
      const parts = [];
      textFiles.forEach(f => {
        parts.push(`[File: ${f.name}]\n${f.content}${f.truncated ? '\n...(content truncated at 100k chars)' : ''}`);
      });
      if (parts.length) fileSuffix += `\n\n--- Attached Files ---\n${parts.join('\n\n---\n\n')}`;
      if (binFiles.length) fileSuffix += `\n\n--- Binary Attachments (no text extraction) ---\n${binFiles.map(f => `${f.name} (${f.contentType})`).join('\n')}`;
      attachRef.current = pendingFiles.map((f) => f.id).filter(Boolean);
      setPendingFiles([]);
    } else {
      attachRef.current = [];
    }
    const fullPrompt = (text || (hasChips ? `Proceed.` : '')) + fileSuffix;

    try {
      // The Home composer is also the workflow authoring entry point. An
      // explicit automation request creates a draft in the normal canvas
      // store, then leaves the user in chat with a clear next step.
      // Home is rendered with Lyra as its host agent, so `isAgent` must not
      // suppress workflow authoring there. Chains are still never converted.
      const workflowDraft = !retryText && !isChain && (embedded || !isAgent) && workflowDraftFromPrompt(text);
      if (workflowDraft) {
        const created = await customAgentAPI.saveFlow(workflowDraft.title, workflowDraft.nodes, workflowDraft.connections);
        const chain = created?.chain || created;
        setHistory(h => [...h, {
          role: "assistant",
          text: workflowDraft.createsTask
            ? `I created **${workflowDraft.title}** as a draft: new ${workflowDraft.channel} messages${workflowDraft.taskTitle ? ` matching the selected filter will create the Productivity task **${workflowDraft.taskTitle}**` : " will create a Productivity task"}. Open **Workflows** to confirm the trigger, connect Gmail if needed, and activate it.`
            : `I created **${workflowDraft.title}** as a draft. It starts with ${workflowDraft.channel}, passes the message to the selected agent, and sends the result through ${workflowDraft.recipient || "the delivery step"}. Open **Workflows** to choose the connected channel and finish the delivery settings before turning it on.`,
          ts: Date.now(),
          isWorkflowDraft: true,
          workflowId: chain?._id || chain?.id,
        }]);
        try { window.dispatchEvent(new CustomEvent("lyra:workflow-created", { detail: { workflow: chain } })); } catch (_) {}
        toast.success("Workflow draft created — review it in Workflows.");
        return;
      }

      // ── Multi-target execution: chips + @ mentions ────────────────────────
      const mentionedTargets = parseMentions(text);

      if (mentionedTargets.length > 1) {
        // Parallel execution of all mentioned targets
        await Promise.all(mentionedTargets.map(async (t) => {
          if (t._type === 'workflow') {
            // Canvas flow
            if (t.source === 'canvas' && t.canvasData?.nodes?.length > 0) {
              await executeFlow(fullPrompt);
            } else {
              // Wizard chain — run steps in-drawer
              await executeChain(fullPrompt, t);
            }
          } else {
            // Agent
            await executeAgent(fullPrompt, t._id);
          }
        }));
      } else if (mentionedTargets.length === 1) {
        // Single mentioned target — use it
        const t = mentionedTargets[0];
        if (t._type === 'workflow') {
          if (t.source === 'canvas' && t.canvasData?.nodes?.length > 0) {
            await executeFlow(fullPrompt);
          } else {
            await executeChain(fullPrompt, t);
          }
        } else {
          await executeAgent(fullPrompt, t._id);
        }
      } else if (isAgent) {
        // No @ mention — use the selected agent if one was actually picked in
        // this conversation, otherwise fall back to a general run rather than
        // silently guessing one of the user's own agents.
        const runAgentId = (target?._id || target?.id) || selectedAgentId;
        if (!runAgentId) {
          await executeGeneral(fullPrompt);
        } else {
          await executeAgent(fullPrompt, runAgentId);
        }
      } else if (target?.source === "canvas" && (target?.canvasData?.nodes?.length > 0)) {
        await executeFlow(fullPrompt);
      } else {
        // Chain/wizard run directly in drawer (no navigate)
        await executeChain(fullPrompt);
      }
    } finally {
      setRunning(false);
      // Clear chips after each send (parallel or single)
      setMentionedChips([]);
    }
  }, [prompt, running, isAgent, isChain, target, pendingFiles, executeAgent, executeGeneral, executeFlow, executeChain, selectedAgentId, parseMentions, mentionedChips, prefsLoaded, hasApiKey]);

  // Keep a stable ref to the latest `send` closure so the (mount-only) prepopulate
  // listener above can trigger a real send after prompt/mentionedChips settle.
  useEffect(() => { sendRef.current = send; }, [send]);

  // Inline "Connect integration" — see INTEGRATION_META above. `retryText`
  // is the original user message that produced the [[NEEDS_INTEGRATION]]
  // reply; once connected, it's resent automatically via send(retryText).
  const handleConnectIntegration = useCallback((integrationId, retryText) => {
    const meta = INTEGRATION_META[integrationId];
    if (!meta || meta.kind === "navigate") {
      navigate(`/integrations#${integrationId}`);
      return;
    }
    if (meta.kind === "modal") {
      setConnectingIntegration({ id: integrationId, retryText });
      return;
    }
    // meta.kind === "oauth" — popup window, not a navigation
    (async () => {
      try {
        const data = await meta.connect();
        const url = data?.[meta.urlField];
        if (!url) throw new Error("No authorization URL returned");
        const popup = window.open(url, "lyra_connect", "width=520,height=680,noopener=no,noreferrer=no");
        if (!popup) {
          toast.error("Popup blocked — allow popups for this site, or connect from Integrations.");
          return;
        }
        const onMessage = (event) => {
          if (event.origin !== window.location.origin) return;
          if (event.data?.type !== "lyra_oauth" || event.data?.provider !== integrationId) return;
          window.removeEventListener("message", onMessage);
          clearInterval(closeWatcher);
          if (event.data.status === "connected") {
            toast.success(`${meta.label} connected!`);
            if (retryText) send(retryText);
          } else {
            toast.error(`Couldn't connect ${meta.label}: ${event.data.msg || "unknown error"}`);
          }
        };
        window.addEventListener("message", onMessage);
        // If the user closes the popup themselves (cancels at the provider's
        // consent screen) without a message ever arriving, clean up instead
        // of leaking the listener forever.
        const closeWatcher = setInterval(() => {
          if (popup.closed) {
            clearInterval(closeWatcher);
            window.removeEventListener("message", onMessage);
          }
        }, 700);
      } catch (err) {
        toast.error(err.response?.data?.error || err.message || `Failed to start ${meta.label} connect`);
      }
    })();
  }, [navigate, send]);

  const onKey = e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };
  const pickMention = (item) => {
    // Instead of inserting @text into prompt, add as a chip and clear the @ token
    const at = prompt.lastIndexOf('@');
    if (at !== -1) setPrompt(prompt.slice(0, at));
    addChip(item);
    setTimeout(() => inputRef.current?.focus(), 50);
  };
  const copyLast   = () => { const last = [...history].reverse().find(m => m.role === "assistant"); if (last) { navigator.clipboard.writeText(last.text); toast.success("Copied!"); } };
  const parseGmailTo = () => { const m = lastPromptRef.current.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/); return m ? m[0] : "" };

  const handleAttachClick = () => { fileInputRef.current?.click(); };

  // Uploads each file (text is extracted server side), shows it as a chip on the composer, and passes it with the next send.
  const uploadFiles = async (files) => {
    const list = [...files].slice(0, 10);
    await Promise.all(list.map(async (f) => {
      setUploading((u) => [...u, f.name]);
      const form = new FormData();
      form.append('file', f);
      try {
        const r = await api.post('/api/attachments/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
        const meta = r.data?.file || r.data;
        setPendingFiles(p => [...p, { blob: f, id: meta.id, name: meta.originalName, contentType: meta.contentType, size: meta.size, content: meta.content, truncated: meta.truncated }]);
      } catch (err) {
        toast.error(`${f.name}: ${err?.response?.data?.error || err.message || 'Upload failed'}`);
      } finally {
        setUploading((u) => { const i = u.indexOf(f.name); return i < 0 ? u : [...u.slice(0, i), ...u.slice(i + 1)]; });
      }
    }));
  };
  const handleFileChange = (e) => {
    const files = e.target.files ? [...e.target.files] : [];
    e.target.value = '';
    if (files.length) uploadFiles(files);
  };
  const onDropFiles = (e) => {
    if (!e.dataTransfer?.files?.length) return;
    e.preventDefault();
    setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };

  // Support opening without a `target` (FAB -> open drawer to select an agent)
  // Do not early-return; show dropdown to pick an agent when no target selected.

  // legacy tab state retained for session persistence; tabs removed in UI

  // If a spaceId is supplied, render the SpaceFeed inside the drawer for reuse
  if (spaceId) {
    return (
      <div
        data-lyra-run-drawer-root="1"
        className="rd-drawer-panel rd-drawer-space"
        style={{
          position: "fixed",
          top: 16,
          right: 16,
          bottom: 16,
          zIndex: 50,
          width: 'min(1120px, calc(100vw - 32px))',
          maxWidth: 1120,
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: 20,
          display: 'flex',
          flexDirection: "column",
          boxShadow: "-4px 0 40px rgba(0,0,0,0.13)",
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: "10px 14px", borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{'Space'}</div>
          <button onClick={() => onClose && onClose()} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}><X size={16} /></button>
        </div>
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          <SpaceFeed spaceId={spaceId} />
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Backdrop (only when drawer is fully open) — not used for the embedded (in-page) variant */}
      {!embedded && !minimized && !isClosing && (
        <div onClick={() => setMinimized(true)} style={{ position: "fixed", inset: 0, zIndex: 40, background: "rgba(20,24,22,0.12)", backdropFilter: "blur(1.5px)" }} />
      )}

      {/* Drawer panel — collapses into FAB (bottom-right) on minimize/close; fills its parent instead when embedded */}
      <div
        data-lyra-run-drawer-root="1"
        className={embedded ? "rd-drawer-embedded" : "rd-drawer-panel"}
        style={embedded ? {
          position: "relative",
          width: "100%",
          height: (compact && history.length === 0 && !running) ? "auto" : "100%",
          background: T.surface,
          display: "flex",
          flexDirection: "column",
          overflow: (showAgentDropdown || showModelPicker) ? 'visible' : 'hidden',
        } : {
          position: "fixed",
          top: 16,
          right: 16,
          bottom: 16,
          zIndex: 50,
          width: 'min(1120px, calc(100vw - 32px))',
          maxWidth: 1120,
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: (minimized || isClosing) ? 999 : 20,
          display: 'flex',
          flexDirection: "column",
          boxShadow: "-4px 0 40px rgba(0,0,0,0.13)",
          transition: "transform .32s cubic-bezier(.4,0,.2,1), opacity .26s ease, border-radius .28s ease",
          overflow: (showAgentDropdown || showModelPicker) ? 'visible' : 'hidden',
          transformOrigin: 'bottom right',
          // 'none' (not scale(1)) when open: any transform makes the panel the containing block of position:fixed popups (Tools, Knowledge, Model), which then land in the wrong place
          transform: (minimized || isClosing) ? 'scale(0.04)' : 'none',
          opacity: (minimized || isClosing) ? 0 : 1,
          pointerEvents: (minimized || isClosing) ? 'none' : 'auto',
        }}
      >
        <style>{`
          @keyframes drawerIn { from { transform: translateX(100%) } to { transform: translateX(0) } }
          @keyframes spin      { to   { transform: rotate(360deg) } }
          @keyframes rdEvalProgress { from { transform: translateX(-145%) } to { transform: translateX(340%) } }

          .rd-drawer-panel {
            left: auto;
          }

          .rd-header {
            display: flex;
            align-items: center;
            gap: 10px;
          }

          .rd-header-actions {
            display: flex;
            align-items: center;
            gap: 2px;
            flex-shrink: 0;
            margin-left: auto;
          }

          .rd-context-toggle {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            height: 32px;
            padding: 0 10px;
            border: 1px solid ${T.border};
            border-radius: 8px;
            background: ${T.surface};
            color: ${T.ink3};
            font: 500 12px/1 inherit;
            cursor: pointer;
          }

          .rd-context-toggle:hover {
            background: ${T.surface2};
            color: ${T.ink};
          }

          .rd-main {
            flex: 1;
            display: flex;
            min-height: 0;
          }

          .rd-left {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
          }

          .rd-right {
            width: 0;
            padding: 0;
            border-left: 0 solid transparent;
            background: ${T.surface};
            overflow: hidden;
            flex-shrink: 0;
            opacity: 0;
            transition: width .22s ease, padding .22s ease, border-color .22s ease, opacity .16s ease;
          }

          .rd-right.rd-right-open {
            width: 320px;
            padding: 12px;
            border-left: 1px solid ${T.border};
            overflow-y: auto;
            opacity: 1;
          }

          .rd-composer-toolbar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            padding: 5px 10px 8px;
          }

          .rd-composer-box {
            border: 1px solid ${T.border};
            border-radius: 14px;
            background: ${T.surface};
            box-shadow: 0 1px 2px rgba(20,24,22,.04);
            transition: border-color .15s ease, box-shadow .15s ease;
          }

          .rd-composer-box:focus-within {
            border-color: ${T.accentMid};
            box-shadow: 0 0 0 3px ${T.accentLight};
          }

          .rd-chat-inner {
            width: min(760px, 100%);
            margin: 0 auto;
          }

          .rd-tools-left,
          .rd-tools-right {
            display: flex;
            align-items: center;
            gap: 4px;
          }

          .rd-chip-row {
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 8px 12px 0;
            flex-wrap: wrap;
            min-height: 36px;
          }

          .rd-textarea {
            width: 100%;
            background: none;
            border: none;
            outline: none;
            resize: none;
            font-family: inherit;
            font-size: 13px;
            color: ${T.ink};
            line-height: 1.6;
            padding: 8px 14px 4px;
            box-sizing: border-box;
            max-height: 130px;
            overflow: auto;
          }

          @media (max-width: 1024px) {
            .rd-drawer-panel {
              top: 8px !important;
              right: 8px !important;
              bottom: 8px !important;
              left: 8px !important;
              width: auto !important;
              max-width: none !important;
              border-radius: 14px !important;
            }

            .rd-main {
              flex-direction: column;
            }

            .rd-right {
              width: 100%;
              border-left: none;
              border-top: 0;
              max-height: 0;
              padding: 0;
              overflow: hidden;
            }

            .rd-right.rd-right-open {
              width: 100%;
              border-top: 1px solid ${T.border};
              max-height: 42%;
              padding: 10px;
              overflow-y: auto;
            }
          }

          @media (max-width: 768px) {
            .rd-drawer-panel {
              overflow-y: auto !important;
              -webkit-overflow-scrolling: touch;
            }

            .rd-header {
              padding: 10px 12px !important;
              align-items: flex-start;
            }

            .rd-header-actions {
              margin-left: auto;
              flex-wrap: nowrap;
              justify-content: flex-end;
              max-width: none;
            }

            .rd-composer-toolbar {
              flex-wrap: wrap;
              gap: 10px;
              padding: 8px 10px 10px;
            }

            .rd-tools-left,
            .rd-tools-right {
              width: 100%;
              flex-wrap: wrap;
              gap: 8px;
            }

            .rd-tools-right {
              justify-content: flex-end;
            }

            .rd-chip-row {
              padding: 10px 10px 2px;
              gap: 8px;
            }

            .rd-textarea {
              font-size: 16px;
              line-height: 1.45;
              padding: 10px 12px 6px;
              max-height: 156px;
            }

            .rd-main {
              display: block;
              min-height: auto;
              flex: initial;
            }

            .rd-left {
              display: block;
              min-height: auto;
            }

            .rd-chat-scroll {
              overflow: visible !important;
              max-height: none !important;
              flex: initial !important;
            }

            .rd-composer {
              position: static !important;
            }

            .rd-context-toggle {
              height: 30px;
              padding: 0 9px;
              font-size: 11px;
            }

            .rd-right {
              width: 100%;
              max-height: 0;
              padding: 0;
              border-top: none;
              overflow: hidden;
              transition: max-height .22s ease, padding .22s ease, border-color .22s ease;
            }

            .rd-right.rd-right-open {
              width: 100%;
              max-height: 50vh;
              padding: 10px;
              border-top: 1px solid ${T.border};
              overflow-y: auto;
            }
          }

          @media (max-width: 520px) {
            .rd-drawer-panel {
              top: var(--lyra-mobile-header-height, 56px) !important;
              right: 0 !important;
              bottom: 0 !important;
              left: 0 !important;
              border-radius: 0 !important;
              border-left: none !important;
              border-right: none !important;
            }

            .rd-right {
              max-height: 60vh;
              padding: 10px;
            }

            .rd-header-actions button {
              width: 30px !important;
              height: 30px !important;
              border-radius: 8px !important;
            }

            .rd-header-actions {
              gap: 4px;
            }

            .rd-context-toggle {
              width: auto !important;
              height: 30px !important;
              padding: 0 9px !important;
            }

            .rd-composer {
              padding: 8px 10px 10px !important;
            }

            .rd-composer-toolbar button,
            .rd-chip-row button {
              min-height: 34px;
            }

            .rd-tools-left > button,
            .rd-tools-left > div > button,
            .rd-tools-right > button {
              flex: 1 1 calc(50% - 6px);
              justify-content: center;
            }

            .rd-tools-right > button:last-child {
              flex: 1 1 100%;
            }
          }
        `}</style>

        {/* (no local minimized FAB — global FAB in Layout handles minimized state) */}

        {/* ── Header ── */}
        {(!embedded || history.length > 0) && (
        <div className="rd-header" style={{ padding: dense ? "4px 10px" : "11px 14px", borderBottom: dense ? "none" : `1px solid ${T.border}`, flexShrink: 0, background: T.surface, ...(headerTarget ? { display: 'none' } : {}) }}>
          {!embedded && (
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: isAgent ? T.accentLight : T.violetBg,
            border: `1px solid ${isAgent ? T.accentMid : T.violetBd}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, flexShrink: 0, overflow: "hidden"
          }}>
            <AgentIcon icon={displayTarget.icon || (isAgent ? "🤖" : "⛓️")} size={36} />
          </div>
          )}

          <div style={{ minWidth: 0, flex: 1 }}>
            {!embedded && (
            <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 650, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {displayTarget.name || "Run"}
              </div>
              <span style={{
                fontSize: 10.5, color: T.ink4, paddingLeft: 7,
                borderLeft: `1px solid ${T.border}`, whiteSpace: "nowrap"
              }}>
                {isChain ? "Workflow" : "Agent"}
              </span>
            </div>
            )}

            {/* Model picker + MCP badge — drawer mode only. In page mode this control lives
                permanently in the composer toolbar instead, so it never jumps position
                when the header appears once a conversation starts. */}
            {!embedded && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2, minWidth: 0 }}>
              {mcpServers.length > 0 && (
                <a href="/mcp-servers"
                  title={`${mcpServers.length} MCP server${mcpServers.length !== 1 ? 's' : ''} active — ${mcpServers.map(s => s.displayName).join(', ')}`}
                  style={{ display: "flex", alignItems: "center", gap: 4, color: T.accent, fontSize: 10.5, fontWeight: 500, textDecoration: "none", cursor: "pointer" }}>
                  <Plug size={10} /> {mcpServers.length} MCP
                </a>
              )}
            </div>
            )}
          </div>

          <HeaderSlot target={headerTarget}>
          <div className="rd-header-actions">
            <button
              onClick={() => setShowContext(v => !v)}
              className="rd-context-toggle"
              title={showContext ? "Hide runtime details" : "Show runtime details"}
              aria-expanded={showContext}
              style={{
                background: showContext ? T.accentLight : T.surface,
                borderColor: showContext ? T.accentMid : T.border,
                color: showContext ? T.accent : T.ink3,
              }}
            >
              <Activity size={13} />
              <span>{showContext ? "Hide context" : "Context"}</span>
              {showContext ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
            </button>

            {history.some(m => m.role === "assistant") && (
              <button onClick={copyLast} title="Copy last response"
                style={{ width: 32, height: 32, borderRadius: 8, border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.ink4 }}>
                <Copy size={13} />
              </button>
            )}

            <button onClick={() => {
              setHistory([]); setRunTrace([]); setRawPayloads([]); setLastResult(null);
              setActiveStepIdx(null); setStepStatuses([]);
              clearSavedSession();
            }} title="Clear chat"
              style={{ width: 32, height: 32, borderRadius: 8, border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.ink4 }}>
              <RefreshCw size={13} />
            </button>

            {embedded ? (
              (showNewTask ? <button onClick={() => {
                setHistory([]); setRunTrace([]); setRawPayloads([]); setLastResult(null);
                setActiveStepIdx(null); setStepStatuses([]); setPrompt(""); setMentionedChips([]);
                clearSavedSession();
              }} title="Start a new task"
                style={{ display: "flex", alignItems: "center", gap: 5, padding: "0 10px", height: 32, borderRadius: 8, border: `1px solid ${T.border}`, background: "transparent", cursor: "pointer", color: T.ink3, fontSize: 12, fontWeight: 600, fontFamily: "inherit" }}>
                <Plus size={13} /> New task
              </button> : null)
            ) : (
              <button onClick={() => { setIsClosing(true); setTimeout(() => { onClose && onClose(); }, 300); }} title="Close"
                style={{ width: 32, height: 32, borderRadius: 8, border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.ink3 }}>
                <X size={14} />
              </button>
            )}
          </div>
          </HeaderSlot>
        </div>
        )}

        {/* ── Removed tabs; using two-column layout (left: conversation, right: context panel) ── */}

        {/* ── Chain progress strip — only for chains, only when a run is active/finished ── */}

        {showChainStrip && (
          <ChainProgressStrip
            steps={
              (displayTarget?.source === "canvas")
                ? ((displayTarget.canvasData?.nodes || []).filter(n => n.agid).map(n => ({ agentId: n.agid, label: n.item?.name || n.brief || 'Agent' })))
                : (displayTarget.steps || [])
            }
            agents={agents}
            statuses={stepStatuses}
            activeIdx={activeStepIdx}
          />
        )}

        {/* ── Main two-column content: left = conversation, right = context panel ── */}
        <div className="rd-main" style={(compact && history.length === 0 && !running) ? { flex: '0 0 auto', width: '100%', height: 'auto', minHeight: 0 } : undefined}>
          {/* LEFT: Conversation area */}
          <div className="rd-left" style={(compact && history.length === 0 && !running) ? { flex: '0 0 auto', width: '100%', height: 'auto' } : undefined}>
            {/* Run banner */}
            {(running || activeStepIdx !== null) && (
              <div style={{ padding: '7px 20px', color: T.ink3, fontSize: 11.5, display: 'flex', alignItems: 'center', gap: 7, borderBottom: `1px solid ${T.border}`, background: T.surface2 }}>
                <span style={{ width: 6, height: 6, borderRadius: 99, background: T.green, boxShadow: `0 0 0 3px ${T.greenBg}` }} />
                {isChain ? "Workflow running" : "Agent running"}
                <span style={{ color: T.ink4 }}>· Live execution</span>
              </div>
            )}

            <div className="rd-chat-scroll" style={{ flex: 1, overflowY: 'auto', padding: dense ? '8px 14px 6px' : '24px 24px 12px', display: (compact && history.length === 0 && !running) ? 'none' : 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="rd-chat-inner" style={{ display: 'flex', flexDirection: 'column', gap: 14, ...(dense ? { width: wideChat ? 'min(1240px, 100%)' : '100%' } : {}) }}>
              {(displayTarget.inputHint || displayTarget.description) && history.length === 0 && (
                <div style={{ margin: '0 0 16px', padding: '12px 14px', borderRadius: 10, background: T.accentLight, border: `1px solid ${T.accentMid}`, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {displayTarget.description && (
                    <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5 }}>{displayTarget.description}</div>
                  )}
                  {displayTarget.inputHint && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.accent, fontWeight: 500 }}>
                      <Paperclip size={12} />
                      {displayTarget.inputHint}
                    </div>
                  )}
                  {displayTarget.requiredInputType === 'file' && (
                    <div style={{ fontSize: 11, color: T.ink4 }}>
                      <span style={{ padding: '2px 8px', borderRadius: 99, background: T.amberBg, color: T.amber, border: `1px solid ${T.amberBd}`, fontWeight: 500 }}>File required</span>
                      {' '}to run this agent
                    </div>
                  )}
                </div>
              )}

              {history.length === 0 && embedded && showEmptyState && (
                <div style={{ textAlign: 'center', padding: '48px 16px 34px' }}>
                  <div style={{ fontSize: 26, fontWeight: 700, color: T.ink, marginBottom: 8 }}>
                    {pageHeading}
                  </div>
                  <div style={{ fontSize: 13.5, color: T.ink3, marginBottom: 22, maxWidth: 480, marginLeft: 'auto', marginRight: 'auto' }}>
                    {pageSubheading}
                  </div>
                  {agentSuggestions.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginBottom: suggestions.length > 0 ? 12 : 0 }}>
                      {agentSuggestions.map((a) => {
                        const aid = a._id || a.id;
                        return (
                          <button
                            key={aid}
                            onClick={() => addChip({ ...a, _type: 'agent' })}
                            title={a.description || a.name}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5,
                              padding: '7px 13px 7px 8px', borderRadius: 999, border: `1px solid ${T.accentMid}`,
                              background: T.accentLight, color: T.accent, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500,
                            }}
                          >
                            <span style={{
                              width: 20, height: 20, borderRadius: '50%', background: T.accentMid, color: T.accent,
                              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0,
                            }}>
                              {(a.icon && !String(a.icon).startsWith('data:')) ? a.icon : (a.name || '?')[0].toUpperCase()}
                            </span>
                            {a.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {suggestions.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                      {suggestions.map((s, i) => {
                        const Icon = s.icon;
                        return (
                          <button key={i} onClick={() => { if (!s.focusOnly) setPrompt(s.label); inputRef.current?.focus(); }}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, padding: '7px 13px', borderRadius: 999, border: `1px solid ${T.border}`, background: T.surface, color: T.ink2, cursor: 'pointer', fontFamily: 'inherit' }}>
                            {Icon && <Icon size={13} />} {s.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {history.length === 0 && !embedded && (
                <div style={{ textAlign: 'center', padding: '48px 16px 34px', color: T.ink4 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.accent, marginBottom: 10 }}>
                    {isChain ? "Workflow ready" : "Ready when you are"}
                  </div>
                  <div style={{ fontSize: 23, fontWeight: 650, letterSpacing: '-0.02em', color: T.ink, marginBottom: 8 }}>
                    What can I help you accomplish?
                  </div>
                  <div style={{ fontSize: 13, color: T.ink3, lineHeight: 1.6, margin: '0 auto 8px', maxWidth: 520 }}>
                    Set a goal, constraints, and output format. Lyra will run and stream the result here.
                  </div>
                  {displayTarget.name && <div style={{ fontSize: 11.5, color: T.ink4, marginBottom: 14 }}>{displayTarget.name}</div>}
                  {displayTarget.description && <div style={{ fontSize: 13, color: T.ink3, lineHeight: 1.6, margin: '0 auto 20px', maxWidth: 520 }}>{displayTarget.description}</div>}
                  {(displayTarget.examples || []).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, justifyContent: 'center' }}>
                      {(displayTarget.examples || []).slice(0, 3).map((ex, i) => (
                        <button key={i} onClick={() => { setPrompt(ex); inputRef.current?.focus(); }}
                          style={{ fontSize: 12, padding: '7px 12px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, color: T.ink2, cursor: 'pointer', fontFamily: 'inherit' }}>
                          {ex}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {history.map((msg, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                  {msg.role === 'assistant' ? (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, maxWidth: '92%' }}>
                      {/* Small agent avatar */}
                      <div style={{ width: 26, height: 26, borderRadius: 8, background: T.accentLight, border: `1px solid ${T.accentMid}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0, marginTop: 2, overflow: 'hidden' }}>
                        <AgentIcon icon={displayTarget.icon || '🤖'} size={26} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ padding: '10px 15px', borderRadius: '6px 18px 18px 18px', background: msg.error ? T.redBg : T.surface, border: `0.5px solid ${msg.error ? T.redBd : T.border}`, borderLeft: `2px solid ${msg.error ? T.red : T.accentMid}`, color: msg.error ? T.red : T.ink, fontSize: 13, lineHeight: 1.7, wordBreak: 'break-word' }}>
                          <div
                            dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.tasks?.length ? (introOf(msg.text) || `You have ${msg.tasks.length} task${msg.tasks.length === 1 ? '' : 's'}:`) : msg.text) }}
                            onClick={(e) => {
                              const a = e.target.closest('a.sandbox-dl-link');
                              if (a) {
                                e.preventDefault();
                                const fn = a.getAttribute('data-filename') || 'file';
                                toast(`📄 "${fn}" was generated in the agent's sandbox — this link type doesn't work outside ChatGPT. Ask the agent to return the document content as text or base64 so it can be saved here.`, { duration: 6000 });
                              }
                            }}
                          />
                          {msg.tasks?.length > 0 && <TaskResultCards tasks={msg.tasks} />}
                        </div>
                        {msg.modelFailure && (
                          <button type="button" onClick={() => setModelFailure(msg.modelFailure)}
                            style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 14px', border: 'none', borderRadius: 9, background: T.accent, color: 'var(--app-on-accent)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                            Fix model keys
                          </button>
                        )}
                        {(msg.approvals || []).map((a) => <ApprovalCard key={a.approvalId} approval={a} />)}
                        {!msg.error && (
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 5, paddingLeft: 2, flexWrap: 'wrap' }}>
                            {msg.modelName && (
                              <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 99, background: T.greenBg, color: T.greenText, border: `1px solid ${T.greenBd}` }}>{msg.modelName}</span>
                            )}
                            {msg.durationMs && (
                              <span style={{ fontSize: 10, color: T.ink4, display: 'flex', alignItems: 'center', gap: 2 }}><Clock size={9} />{msg.durationMs}ms</span>
                            )}
                            {msg.mcpToolCount > 0 && (
                              <span title={(msg.mcpCalls || []).join('\n')} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 99, background: T.accentLight, color: T.accent, border: `1px solid ${T.accentMid}`, display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Plug size={9} />
                                {msg.mcpCalls && msg.mcpCalls.length > 0
                                  ? <>{msg.mcpCalls.slice(0, 2).join(', ')}{msg.mcpCalls.length > 2 ? ` +${msg.mcpCalls.length - 2}` : ''}</>
                                  : <>{msg.mcpToolCount} MCP tool{msg.mcpToolCount !== 1 ? 's' : ''}</>}
                              </span>
                            )}
                            {msg.knowledgeNote && (
                              <span role="status" style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: 'var(--lyra-color-warning-bg)', color: 'var(--lyra-color-warning)', border: '0.5px solid var(--app-warning-border)' }}>
                                Your documents were not used: {msg.knowledgeNote}
                              </span>
                            )}
                            {msg.citations && msg.citations.length > 0 && (
                              <span title="Passages from your Knowledge Spaces used for this answer" style={{ fontSize: 10, color: T.ink3, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                                Sources: {msg.citations.map((c) => <span key={c.n} style={{ padding: '1px 6px', borderRadius: 99, background: T.accentLight, color: T.accent, border: `1px solid ${T.accentMid}` }}>[{c.n}] {c.filename}{c.page != null ? `, p.${c.page}` : ''}</span>)}
                              </span>
                            )}
                            <button onClick={() => { navigator.clipboard.writeText(msg.text); toast.success('Copied!'); }}
                              style={{ fontSize: 10, color: T.ink4, background: 'none', border: `1px solid ${T.border}`, borderRadius: 5, padding: '1px 7px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 3 }}>
                              <Copy size={9} /> Copy
                            </button>
                            {tgConnected && !msg.error && (
                              <button onClick={() => sendToTelegram(msg.text)} title="Send this reply to your Telegram"
                                style={{ fontSize: 10, color: '#229ED9', background: 'none', border: `1px solid ${T.border}`, borderRadius: 5, padding: '1px 7px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Send size={9} /> Telegram
                              </button>
                            )}
                            {isEmailAgent && gmailStatus?.connected && (
                              <button onClick={() => { setLastResult({ result: msg.text }); setShowGmailModal(true); }}
                                style={{ fontSize: 10, color: T.red, background: T.redBg, border: `1px solid ${T.redBd}`, borderRadius: 5, padding: '1px 7px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Mail size={9} /> Send
                              </button>
                            )}
                          </div>
                        )}

                        {msg.needsIntegration && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, padding: '9px 12px', borderRadius: 10, background: T.amberBg, border: `1px solid ${T.amberBd}` }}>
                            <Plug size={13} color={T.amber} style={{ flexShrink: 0 }} />
                            <span style={{ fontSize: 12, color: T.ink2, flex: 1 }}>
                              This needs {INTEGRATION_LABELS[msg.needsIntegration] || msg.needsIntegration} connected.
                            </span>
                            <button
                              onClick={() => {
                                const priorUserText = history.slice(0, i).reverse().find(m => m.role === 'user')?.text || null;
                                handleConnectIntegration(msg.needsIntegration, priorUserText);
                              }}
                              style={{ flexShrink: 0, border: 'none', borderRadius: 7, padding: '5px 11px', background: T.accent, color: 'var(--app-on-accent)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                            >
                              Connect
                            </button>
                          </div>
                        )}

                        {msg.suggestions && msg.suggestions.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                            {msg.suggestions.map((s, si) => (
                              <button
                                key={si}
                                onClick={() => { setPrompt(s); inputRef.current?.focus(); }}
                                style={{ border: `1px solid ${T.border}`, background: T.surface, color: T.ink2, borderRadius: 999, padding: '5px 11px', fontSize: 11.5, cursor: 'pointer', fontFamily: 'inherit' }}
                              >
                                {s}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, maxWidth: '82%' }}>
                      <AttachmentCards files={msg.attachments} onOpen={setViewing} />
                      <div style={{ padding: '10px 16px', borderRadius: '18px 18px 5px 18px', background: T.accent, color: 'var(--app-on-accent)', fontSize: 13, lineHeight: 1.7, wordBreak: 'break-word' }}>
                        <span style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {running && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: T.ink4, fontSize: 12 }}>
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite', color: isAgent ? T.accent : T.violet }} />
                  {activeAgent?.name || 'Agent'} is thinking…
                </div>
              )}
                <div ref={scrollRef} />
              </div>
            </div>

            {/* ── Composer (Conversations.tsx style) ── */}
            <div className="rd-composer" style={{ borderTop: dense ? 'none' : `1px solid ${T.border}`, flexShrink: 0, background: T.surface, padding: dense ? '4px 8px 8px' : '10px 18px 14px', position: 'relative' }}>
              <div className="rd-composer-box">

              {/* ── Mention suggestions dropdown ── */}
              {mentionSuggestions.length > 0 && (
                <div style={{ position: 'absolute', left: 14, right: 14, bottom: 'calc(100% + 4px)', maxHeight: 260, background: T.surface, border: `1px solid ${T.border2}`, borderRadius: 12, boxShadow: '0 10px 30px rgba(16,24,40,0.12)', zIndex: 200, overflow: 'hidden' }}>
                  <div style={{ padding: '6px 12px', fontSize: 11, fontWeight: 600, color: T.ink4, borderBottom: `1px solid ${T.border}`, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Mention</div>
                  <div style={{ maxHeight: 210, overflowY: 'auto' }}>
                    {mentionSuggestions.map((s, idx) => (
                      <div key={s._id || s.id || idx} onMouseDown={e => { e.preventDefault(); pickMention(s); }}
                        style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, transition: 'background .1s' }}
                        onMouseEnter={e => e.currentTarget.style.background = T.surface2}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <div style={{ width: 30, height: 30, borderRadius: '50%', background: s._type === 'workflow' ? T.violetBg : T.accentLight, border: `1px solid ${s._type === 'workflow' ? T.violetBd : T.accentMid}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: s._type === 'workflow' ? T.violet : T.accent, flexShrink: 0 }}>
                          {(s.name || '?')[0].toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                            <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 6, background: s._type === 'workflow' ? T.violetBg : T.accentLight, color: s._type === 'workflow' ? T.violet : T.accent, border: `1px solid ${s._type === 'workflow' ? T.violetBd : T.accentMid}`, flexShrink: 0 }}>{s._type}</span>
                          </div>
                          {s.description && <div style={{ fontSize: 11, color: T.ink4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>{s.description}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── @ agents/workflow picker dropdown ── */}
              {showAgentDropdown && (
                <div ref={dropdownRef} style={{ ...(agentPos || { position: 'fixed', visibility: 'hidden' }), background: T.surface, border: `1px solid ${T.border2}`, borderRadius: 12, boxShadow: '0 10px 30px rgba(16,24,40,0.12)', zIndex: 200, overflow: 'hidden' }}>
                  <div style={{ padding: '8px 12px', borderBottom: `1px solid ${T.border2}` }}>
                    <input value={dropdownSearch} onChange={e => { setDropdownSearch(e.target.value); setFocusedIndex(-1); }}
                      onKeyDown={e => {
                        const allItems = agentWorkflowList.length > 0 ? agentWorkflowList : (agents || []).map(a => ({ ...a, _type: 'agent' }));
                        const q = (dropdownSearch || '').trim().toLowerCase();
                        const filtered = allItems.filter(a => { if (!a) return false; if (!q) return true; return (a.name || '').toLowerCase().includes(q) || (a.description || '').toLowerCase().includes(q); });
                        if (e.key === 'ArrowDown') { e.preventDefault(); setFocusedIndex(i => Math.min(filtered.length - 1, Math.max(0, i === -1 ? 0 : i + 1))); }
                        else if (e.key === 'ArrowUp') { e.preventDefault(); setFocusedIndex(i => Math.max(-1, i === -1 ? -1 : i - 1)); }
                        else if (e.key === 'Enter') {
                          e.preventDefault();
                          const idx = focusedIndex >= 0 ? focusedIndex : 0;
                          const pick = filtered[idx];
                          if (pick) {
                            addChip(pick);
                            setShowAgentDropdown(false); setDropdownSearch(''); setFocusedIndex(-1);
                          }
                        } else if (e.key === 'Escape') { setShowAgentDropdown(false); setFocusedIndex(-1); }
                      }}
                      autoFocus
                      placeholder="Search agents & workflows…"
                      style={{ width: '100%', padding: '7px 11px', borderRadius: 8, border: `1px solid ${T.border2}`, outline: 'none', fontFamily: 'inherit', fontSize: 12, background: T.surface2, color: T.ink2 }} />
                  </div>
                  <div style={{ maxHeight: 268, overflowY: 'auto' }}>
                    {(() => {
                      const allItems = agentWorkflowList.length > 0 ? agentWorkflowList : (agents || []).map(a => ({ ...a, _type: 'agent' }));
                      const q = dropdownSearch.trim().toLowerCase();
                      const filtered = allItems.filter(a => { if (!a) return false; if (!q) return true; return (a.name || '').toLowerCase().includes(q) || (a.description || '').toLowerCase().includes(q); });
                      if (filtered.length === 0) return <div style={{ padding: '12px 14px', color: T.ink4, fontSize: 12 }}>No agents or workflows found</div>;
                      return filtered.map((a, idx) => (
                        <div key={(a && (a._id || a.id)) || idx}
                          onMouseEnter={() => setFocusedIndex(idx)}
                          onMouseDown={e => {
                            // mousedown (not click): the outside-click-close listener below also
                            // fires on mousedown, and would unmount this dropdown — and this row
                            // with it — before a click event ever reached it, silently dropping
                            // the selection. preventDefault keeps focus from jumping away too.
                            e.preventDefault();
                            addChip(a); setShowAgentDropdown(false); setDropdownSearch(''); setFocusedIndex(-1);
                            setTimeout(() => inputRef.current?.focus(), 50);
                          }}
                          style={{ padding: '9px 12px', cursor: 'pointer', borderBottom: `1px solid rgba(0,0,0,0.04)`, display: 'flex', alignItems: 'center', gap: 10, background: focusedIndex === idx ? T.surface2 : 'transparent', transition: 'background .12s' }}>
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: a._type === 'workflow' ? T.violetBg : T.accentLight, border: `1px solid ${a._type === 'workflow' ? T.violetBd : T.accentMid}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: a._type === 'workflow' ? T.violet : T.accent, flexShrink: 0 }}>
                            {a._type === 'workflow' ? '⛓' : ((a?.icon && !String(a.icon).startsWith('data:')) ? a.icon : (a?.name || '?')[0].toUpperCase())}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a?.name}</span>
                              <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 5px', borderRadius: 5, background: a._type === 'workflow' ? T.violetBg : T.accentLight, color: a._type === 'workflow' ? T.violet : T.accent, border: `1px solid ${a._type === 'workflow' ? T.violetBd : T.accentMid}`, flexShrink: 0 }}>{a._type}</span>
                            </div>
                            {a?.description && <div style={{ fontSize: 11, color: T.ink4, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.description}</div>}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}

              {modelFailure && (
                <ModelFailureModal
                  failure={modelFailure}
                  onClose={() => setModelFailure(null)}
                  onGoToModels={() => {
                    try { if (prompt.trim()) sessionStorage.setItem("lyra:pending-prompt", prompt); } catch (e) { /* storage blocked */ }
                    setModelFailure(null);
                    navigate("/ai-console");
                  }}
                />
              )}
              {keyPromptOpen && (
                <ApiKeyPrompt
                  onDismiss={() => setKeyPromptOpen(false)}
                  onGoToModels={() => {
                    // Keep what they typed: it is put back in the composer when they return.
                    try { if (prompt.trim()) sessionStorage.setItem("lyra:pending-prompt", prompt); } catch (e) { /* storage blocked: they retype */ }
                    navigate("/ai-console");
                  }}
                />
              )}

              {/* ── Composer card ── */}
              <div
                onDragOver={(e) => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setDragOver(true); } }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDropFiles}
                style={{ position: 'relative', border: `1px ${dragOver ? 'dashed' : 'solid'} ${dragOver ? 'var(--lyra-color-primary)' : T.border2}`, borderRadius: 14, background: dragOver ? 'var(--app-primary-soft)' : T.surface, overflow: showModelPicker ? 'visible' : 'hidden' }}>

                {embedded && onExpand && (
                  <button type="button" title="Open full screen" aria-label="Open full screen"
                    onClick={() => { try { sessionStorage.setItem('lyra:composer:files', JSON.stringify(pendingFiles)); } catch (e) { /* too large to carry: they are attached again */ } onExpand({ prompt }); }}
                    style={{ position: 'absolute', top: 8, right: 8, zIndex: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, border: `1px solid ${T.border}`, borderRadius: 8, background: T.surface, color: T.ink3, cursor: 'pointer' }}>
                    <Maximize2 size={14} />
                  </button>
                )}

                {onAddDomainAgent && onRemoveDomainAgent && (
                  <DomainAgentChips selected={domainAgents} available={availableDomainAgents} onAdd={onAddDomainAgent} onRemove={onRemoveDomainAgent} />
                )}

                {/* Row 1: Agent / workflow chips — only rendered once something is attached;
                    the toolbar's "+ Agent" button below is the one trigger to open the picker. */}
                {mentionedChips.length > 0 && (
                  <div className="rd-chip-row">
                    {mentionedChips.map((chip, i) => (
                      <div key={chip._id || i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px 3px 6px', borderRadius: 999, background: chip._type === 'workflow' ? T.violetBg : T.accentLight, border: `1px solid ${chip._type === 'workflow' ? T.violetBd : T.accentMid}`, fontSize: 12, fontWeight: 600, color: chip._type === 'workflow' ? T.violet : T.accent }}>
                        <div style={{ width: 18, height: 18, borderRadius: '50%', background: chip._type === 'workflow' ? T.violetBd : T.accentMid, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: chip._type === 'workflow' ? T.violet : T.accent, flexShrink: 0 }}>
                          {(chip.name || '?')[0].toUpperCase()}
                        </div>
                        <span>{chip.name}</span>
                        <button onMouseDown={e => { e.preventDefault(); removeChip(chip._id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: chip._type === 'workflow' ? T.violet : T.accent, opacity: 0.7, fontSize: 13, lineHeight: 1 }}>×</button>
                      </div>
                    ))}
                  </div>
                )}

                {<PickChips picks={picks} files={pendingFiles} uploading={uploading} onRemoveFile={(i) => setPendingFiles((p) => p.filter((_, j) => j !== i))} />}

                {/* Row 2: Textarea */}
                <textarea ref={inputRef} value={prompt}
                  onChange={e => {
                    setPrompt(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 130) + 'px';
                  }}
                  onKeyDown={onKey}
                  placeholder={mentionedChips.length > 0 ? `Message ${mentionedChips.map(c => `@${c.name}`).join(', ')}…` : `Message… type @name to mention`}
                  rows={1}
                  className="rd-textarea"
                />

                {/* Row 3: Toolbar */}
                <div className="rd-composer-toolbar">
                  {/* Left tools */}
                  <div className="rd-tools-left">
                    {/* Model picker — lives here permanently in page mode (never in the
                        header, so it doesn't jump position once a conversation starts);
                        the drawer header hosts the same control everywhere else. */}
                    {(
                      <div style={{ position: 'relative' }} ref={pickerRef}>
                        <button
                          onClick={() => setShowModelPicker(p => !p)}
                          aria-label={`Model: ${modelLabel}`}
                          title="Choose model"
                          style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface2, cursor: 'pointer', fontSize: 12, color: effectiveModelId ? T.accent : T.ink3, fontFamily: 'inherit', fontWeight: 500 }}
                        >
                          <Cpu size={12} />
                          <span>{modelLabel}</span>
                          <ChevronDown size={10} />
                        </button>

                        {showModelPicker && (
                          <div style={{
                            ...(modelPos || { position: "fixed", visibility: "hidden" }),
                            background: T.surface, border: `0.5px solid var(--app-border-strong)`,
                            borderRadius: 12, boxShadow: "0 14px 34px rgba(8,12,24,.22)",
                            zIndex: 140, overflowY: "auto", padding: "5px 0"
                          }}>
                            <div style={{ padding: "8px 13px 6px", fontSize: 10.5, fontWeight: 700, color: T.ink4, textTransform: "uppercase", letterSpacing: ".05em" }}>
                              Model
                            </div>
                            <button
                              onClick={() => { setLocalModelId(null); setShowModelPicker(false); }}
                              style={{
                                width: "100%", textAlign: "left", padding: "9px 13px",
                                border: "none", background: effectiveMode === "auto" ? T.accentLight : "transparent",
                                color: effectiveMode === "auto" ? T.accent : T.ink2,
                                fontSize: 12, fontWeight: effectiveMode === "auto" ? 650 : 450,
                                cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 8
                              }}
                            >
                              <span style={{ width: 22, height: 22, borderRadius: 6, background: T.surface2, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>⚡</span>
                              <span style={{ flex: 1 }}>
                                <span style={{ display: "block" }}>Auto</span>
                                <span style={{ display: "block", fontSize: 10.5, color: T.ink4, fontWeight: 400, marginTop: 1 }}>Lyra chooses the best model</span>
                              </span>
                              {effectiveMode === "auto" && <Check size={12} color={T.accent} />}
                            </button>

                            <div style={{ height: 1, background: T.border, margin: "5px 0" }} />

                            <div style={{ padding: "5px 13px 4px", fontSize: 10.5, fontWeight: 700, color: T.ink4, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                              <span>{curatedModels ? "My models" : "Advanced models"}</span>
                              {hasWorkingUserModel && (
                                <button onClick={() => setShowAllModels(v => !v)} style={{ border: "none", background: "transparent", color: T.accent, fontSize: 10.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
                                  {showAllModels ? "Show mine" : "Show all"}
                                </button>
                              )}
                            </div>

                            {pickerModels.map(m => (
                              <button key={m.id} onClick={() => { setLocalModelId(m.id); setShowModelPicker(false); }}
                                style={{
                                  width: "100%", textAlign: "left", padding: "8px 13px",
                                  border: "none", background: effectiveModelId === m.id ? T.accentLight : "transparent",
                                  color: effectiveModelId === m.id ? T.accent : T.ink2,
                                  fontSize: 12, fontWeight: effectiveModelId === m.id ? 600 : 400,
                                  cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 8
                                }}
                              >
                                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.name}</span>
                                {m.badge && (
                                  <span style={{
                                    fontSize: 9, fontWeight: 700, padding: "2px 5px", borderRadius: 4,
                                    background: m.badge === "FREE" ? T.greenBg : T.violetBg,
                                    color: m.badge === "FREE" ? T.green : T.violet
                                  }}>{m.badge}</span>
                                )}
                                {effectiveModelId === m.id && <Check size={11} color={T.accent} />}
                              </button>
                            ))}

                            <div style={{ height: 1, background: T.border, margin: "5px 0" }} />
                            <a href="/ai-console" onClick={e => { e.preventDefault(); window.location.href = "/ai-console"; }}
                              style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 13px", fontSize: 11.5, color: T.ink4, textDecoration: "none", fontFamily: "inherit" }}>
                              <Settings size={11} /> Manage API keys
                            </a>
                          </div>
                        )}
                      </div>
                    )}

                    {/* @ agents/workflow button — the one trigger for the picker (see Row 1 above) */}
                    <button ref={dropdownWrapperRef} onClick={() => setShowAgentDropdown(s => !s)} title="Mention an agent or workflow"
                      style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface2, cursor: 'pointer', fontSize: 12, color: T.ink3, fontFamily: 'inherit', fontWeight: 500 }}>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>+</span>
                      <span>Agent</span>
                    </button>

                    {/* Tools: the skill registry in page mode; the older personal-tools list in the floating drawer */}
                    <ToolsPicker picks={picks} />
                    <KnowledgePicker picks={picks} />

                    {/* Attach */}
                    <button onClick={handleAttachClick} title="Attach files (or drop them here)"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface2, cursor: 'pointer', fontSize: 12, color: T.ink3, fontFamily: 'inherit', fontWeight: 500 }}>
                      <Paperclip size={12} color={T.ink4} />
                      <span>+ Files{pendingFiles.length > 0 ? ` (${pendingFiles.length})` : ''}</span>
                    </button>
                  </div>

                  {/* Right: Stop / Send */}
                  <div className="rd-tools-right" style={{ gap: 6 }}>
                    {running && (
                      <button onClick={() => { try { window.dispatchEvent(new CustomEvent('lyra:run-stop-request')); } catch (e) {} setRunning(false); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 8, background: 'rgba(255,235,235,.9)', border: '1px solid rgba(220,50,50,.3)', color: '#c0392b', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                        <span style={{ fontSize: 11 }}>⏹</span> Stop
                      </button>
                    )}
                    <button onClick={send} disabled={(!prompt.trim() && mentionedChips.length === 0) || running}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 14px', borderRadius: 8, background: (prompt.trim() || mentionedChips.length > 0) && !running ? T.accent : T.border, border: 'none', color: (prompt.trim() || mentionedChips.length > 0) && !running ? 'var(--lyra-color-surface)' : T.ink4, fontSize: 12, fontWeight: 600, cursor: (prompt.trim() || mentionedChips.length > 0) && !running ? 'pointer' : 'default', fontFamily: 'inherit', transition: 'background .15s' }}>
                      {running
                        ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
                        : <><Send size={12} style={{ marginRight: 2 }} /> Send</>
                      }
                    </button>
                  </div>
                </div>
              </div>

              <input type="file" multiple ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} />
              {viewing && <AttachmentViewer file={viewing} onClose={() => setViewing(null)} />}
              </div>
            </div>

          </div>

          {/* RIGHT: Context panel (stacked collapsible sections) */}
          <div className={`rd-right ${showContext ? "rd-right-open" : ""}`} style={(compact && history.length === 0 && !running) ? { display: 'none' } : undefined}>
            {embedded && headerTarget ? (
              <HomeRunEvaluation runId={lastResult?.agentRunId} />
            ) : (
              <ContextSections
                runTrace={runTrace}
                rawPayloads={rawPayloads}
                runMemories={runMemories}
                setRunMemories={setRunMemories}
                lastResult={lastResult}
                modelLabel={modelLabel}
                running={running}
                activeStepIdx={activeStepIdx}
                displayTarget={displayTarget}
              />
            )}
          </div>
        </div>

        {/* old per-tab panes removed — replaced by the right-hand ContextSections */}
      </div>

      {/* Previously showed a local minimized FAB here. Now we emit events so Layout renders a single global FAB. */}

      {/* Gmail modal */}
      {showGmailModal && lastResult && (
        <GmailModal
          result={lastResult.result || lastResult}
          fromEmail={gmailStatus?.email}
          initialTo={parseGmailTo()}
          onClose={() => setShowGmailModal(false)}
        />
      )}

      {/* Inline "Connect integration" modal (see INTEGRATION_META above) —
          opened from a [[NEEDS_INTEGRATION]] card without leaving this chat. */}
      {connectingIntegration && INTEGRATION_META[connectingIntegration.id]?.Modal && (() => {
        const Modal = INTEGRATION_META[connectingIntegration.id].Modal;
        const retryText = connectingIntegration.retryText;
        return (
          <Modal
            onClose={() => setConnectingIntegration(null)}
            onSaved={() => {
              setConnectingIntegration(null);
              if (retryText) send(retryText);
            }}
          />
        );
      })()}
    </>
  );
}
